/* "Recolor" (Replace Colors) — swap or erase colors in a design image.
   Ported from the recolor-feature spec (Justin 7-9). Two surfaces share ONE
   dialog + engine via adapters:

   1. Design Studio (fabric.js) — .tft-recolor button in the image context
      pill (never text). Result re-hosted via presign, fabric src swapped.
   2. By-size PDPs (Justin 7-9): dtf-transfers (by-size-middle-scroll),
      dtf-transfers-by-size-v2 (power-builder), uv-dtf-stickers-decals-by-size
      (uv-by-size-middle-scroll) — a dashed "Recolor" button injected under
      Edit with AI in the AI toolbar. Apply feeds the recolored file through
      the SAME pipeline Edit with AI uses (processFileOrUrl +
      singleFileManageByURL / updateFileDetailsFunction), so preview, sizing,
      pricing and cart data all update exactly like an AI edit.

   Engine (shared math, client-side):
   - CIELAB/CIE76 segmentation: pixels assigned to nearest extracted-palette
     color; per-color tolerance = signed boundaryBias; 3dE core floor;
     feathered boundary weights keep edges smooth.
   - Dialog previews on a <=1024px working copy; Apply re-runs the identical
     applyColorReplacements() at full resolution (<=4000px).
   - Non-destructive: pre-recolor source + replacements stored per target
     (fabric object props / PDP fileDetails), so re-editing never stacks and
     Reset restores the original. In-modal undo/redo steps through actions.

   Loaded from snippets/v0-editor.liquid (present on all studio surfaces AND
   the by-size PDPs via theme.liquid's v0-editor render). */
(function () {
  'use strict';
  /* By Size v5 (11-03): this same file also runs inside a background worker for the full-size Swap Colors render
     (recolorWorker below), so Apply never freezes the page. In the worker there is no page: the dialog, buttons and
     listeners are skipped and only the colour code + a message handler run. Same code both sides = same pixels. */
  var IN_WORKER = typeof document === 'undefined' && typeof importScripts === 'function';
  if (!IN_WORKER) {
    if (window.__studioRecolorBound) return;
    window.__studioRecolorBound = true;
  }
  /* our own <script src>, captured at parse time — the shared Image Filters
     engine (assets/nt-filters.js, built from dev/studio-v2/src/filters.js)
     is loaded on demand from the same asset dir, see loadNTFilters() */
  var RCL_SRC = IN_WORKER ? '' : (document.currentScript && document.currentScript.src) || (function () {
    var sc = document.querySelector('script[src*="studio-recolor"]');
    return sc ? sc.src : '';
  })();

  var MAX_WORKING = 1024;      /* preview working copy bound */
  var MAX_SOURCE = 4000;       /* full-res processing bound */
  var MAX_SWATCHES = 10;
  var MIN_COVERAGE = 5;        /* % — drop swatches below this (keep the top one). 2 -> 5 (Justin 7-10: sub-5% slivers cluttered the picker) */
  var SEGMENT_CORE_KEEP = 3;   /* dE — always replace pixels this close to the target */
  var SEGMENT_FEATHER = 5;     /* dE band over which the weight ramps 1 -> 0 */
  var MERGE_DE = 5;            /* collapse extracted clusters closer than this */
  var BIAS_LIMIT = 50;

  /* The three PDP templates that get the toolbar Recolor button. */
  var PDP_TEMPLATES = /template-product-(by-size-middle-scroll|uv-by-size-middle-scroll|power-builder)(\s|$)/;

  var PRESETS = [
    '#FFFFFF', '#D9D9D9', '#A6A6A6', '#737373', '#404040', '#000000', '#7A5C43', '#C69C6D',
    '#FF0000', '#E2231A', '#B71C1C', '#FF6900', '#FF9E1B', '#FFC72C', '#FFF200', '#F5E1A4',
    '#00A651', '#2E7D32', '#8DC63F', '#00B08B', '#00FFFF', '#00AEEF', '#019AFF', '#0057B8',
    '#003087', '#1B1464', '#7C3AED', '#92278F', '#C724B1', '#EC008C', '#F57EB6', '#FFB6C1',
    '#8B0000', '#800020', '#654321', '#D2B48C', '#F5F5DC', '#C0C0C0', '#B87333', '#D4AF37',
    '#FF6F61', '#E97451', '#CC5500', '#DAA520', '#EEDC82', '#808000', '#9ACD32', '#0B6623',
    '#98FF98', '#40E0D0', '#87CEEB', '#5DADEC', '#4169E1', '#27408B', '#6A0DAD', '#B57EDC',
    '#FF1493', '#DDA0DD', '#708090', '#36454F'
  ];

  /* ─────────────────────────── color math ─────────────────────────── */

  function hexToRgb(hex) {
    var h = String(hex).replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function rgbToHex(r, g, b) {
    return '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1).toUpperCase();
  }
  /* sRGB -> CIELAB (D65) */
  function rgbToLab(r, g, b) {
    var sr = r / 255, sg = g / 255, sb = b / 255;
    sr = sr > 0.04045 ? Math.pow((sr + 0.055) / 1.055, 2.4) : sr / 12.92;
    sg = sg > 0.04045 ? Math.pow((sg + 0.055) / 1.055, 2.4) : sg / 12.92;
    sb = sb > 0.04045 ? Math.pow((sb + 0.055) / 1.055, 2.4) : sb / 12.92;
    var x = (sr * 0.4124564 + sg * 0.3575761 + sb * 0.1804375) / 0.95047;
    var y = (sr * 0.2126729 + sg * 0.7151522 + sb * 0.0721750);
    var z = (sr * 0.0193339 + sg * 0.1191920 + sb * 0.9503041) / 1.08883;
    x = x > 0.008856 ? Math.cbrt(x) : (7.787 * x) + 16 / 116;
    y = y > 0.008856 ? Math.cbrt(y) : (7.787 * y) + 16 / 116;
    z = z > 0.008856 ? Math.cbrt(z) : (7.787 * z) + 16 / 116;
    return [(116 * y) - 16, 500 * (x - y), 200 * (y - z)];
  }
  /* Lab -> sRGB (for luminance-preserving recolor) */
  function labToRgb(L, a, bb) {
    var y = (L + 16) / 116, x = a / 500 + y, z = y - bb / 200;
    var x3 = x * x * x, y3 = y * y * y, z3 = z * z * z;
    x = 0.95047 * (x3 > 0.008856 ? x3 : (x - 16 / 116) / 7.787);
    y = (y3 > 0.008856 ? y3 : (y - 16 / 116) / 7.787);
    z = 1.08883 * (z3 > 0.008856 ? z3 : (z - 16 / 116) / 7.787);
    var r = x * 3.2404542 + y * -1.5371385 + z * -0.4985314;
    var g = x * -0.9692660 + y * 1.8760108 + z * 0.0415560;
    var b2 = x * 0.0556434 + y * -0.2040259 + z * 1.0572252;
    function gam(c) { c = c > 0.0031308 ? 1.055 * Math.pow(c, 1 / 2.4) - 0.055 : 12.92 * c; return Math.max(0, Math.min(255, Math.round(c * 255))); }
    return [gam(r), gam(g), gam(b2)];
  }
  function deltaE(l1, l2) {
    var dL = l1[0] - l2[0], da = l1[1] - l2[1], db = l1[2] - l2[2];
    return Math.sqrt(dL * dL + da * da + db * db);
  }
  function smoothstep(t) { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); }

  /* ───────────────── shared core: applyColorReplacements ─────────────────
     Mutates `data` (Uint8ClampedArray, RGBA). Same function runs on the
     preview working copy AND the full-res source. */
  function applyColorReplacements(data, replacements, palette) {
    replacements = (replacements || []).filter(function (r) { return r && r.type !== 'shape'; });
    if (!replacements.length) return;
    var palLab = palette.map(function (h) { var c = hexToRgb(h); return rgbToLab(c[0], c[1], c[2]); });
    var palRepl = palette.map(function (h) {
      var up = h.toUpperCase();
      for (var i = 0; i < replacements.length; i++) {
        if (replacements[i].targetColor.toUpperCase() === up) return replacements[i];
      }
      return null;
    });
    var newRgb = replacements.map(function (r) { return r.mode === 'recolor' ? hexToRgb(r.newColor) : null; });
    var newLab = newRgb.map(function (c) { return c ? rgbToLab(c[0], c[1], c[2]) : null; });
    var replIdxByPal = palRepl.map(function (r) { return r ? replacements.indexOf(r) : -1; });

    /* memoize decisions per unique rgb — art has few unique colors vs pixels */
    var memo = new Map();
    var n = data.length;
    for (var p = 0; p < n; p += 4) {
      var alpha = data[p + 3];
      if (alpha < 2) continue; /* skip already-transparent */
      var r = data[p], g = data[p + 1], b = data[p + 2];
      var key = (r << 16) | (g << 8) | b;
      var dec = memo.get(key);
      if (dec === undefined) {
        var lab = rgbToLab(r, g, b);
        var d1 = Infinity, d2 = Infinity, i1 = -1;
        for (var i = 0; i < palLab.length; i++) {
          var d = deltaE(lab, palLab[i]);
          if (d < d1) { d2 = d1; d1 = d; i1 = i; }
          else if (d < d2) { d2 = d; }
        }
        dec = null;
        if (i1 > -1 && replIdxByPal[i1] > -1) {
          var repl = palRepl[i1];
          var bias = Math.max(-BIAS_LIMIT, Math.min(BIAS_LIMIT, repl.boundaryBias || 0));
          var margin = (d2 === Infinity ? SEGMENT_FEATHER : (d2 - d1)) + bias;
          var w = smoothstep(margin / SEGMENT_FEATHER);
          var dTarget = deltaE(lab, palLab[i1]);
          if (dTarget <= SEGMENT_CORE_KEEP) w = 1; /* core floor */
          if (w > 0) {
            var ri = replIdxByPal[i1];
            var out = null;
            if (repl.mode === 'transparent') {
              dec = { t: 1, w: w };
            } else if (repl.flatFill !== false) {
              out = newRgb[ri];
              dec = { t: 0, w: w, r: out[0], g: out[1], b: out[2] };
            } else {
              /* luminance-preserving: keep pixel L, move a/b toward the new color */
              var nl = newLab[ri];
              var mixed = labToRgb(lab[0], lab[1] + (nl[1] - lab[1]) * w, lab[2] + (nl[2] - lab[2]) * w);
              dec = { t: 0, w: 1, r: mixed[0], g: mixed[1], b: mixed[2] };
            }
          }
        }
        memo.set(key, dec || false);
        if (!dec) continue;
      } else if (dec === false) {
        continue;
      }
      if (dec.t === 1) {
        data[p + 3] = Math.round(alpha * (1 - dec.w));
      } else {
        var w2 = dec.w;
        data[p]     = Math.round(r + (dec.r - r) * w2);
        data[p + 1] = Math.round(g + (dec.g - g) * w2);
        data[p + 2] = Math.round(b + (dec.b - b) * w2);
      }
    }
  }

  /* ───────────────── Recolor v2: edits on a part or on one color ─────────────────
     Claude Design handoff "Recolor modal v2" (Justin 2026-10-29): one flow, no tabs. Tap a swatch = that color
     everywhere; tap the image = only the connected part (an empty spot = fill it). Every edit is stored as
       { type:'shape', scope:'part', fx, fy, tol, target, seedT, via }   fx/fy = tap point as a FRACTION
       { type:'shape', scope:'all',  color, tol, target }                 color = the swatch color
     (target '#RRGGBB' | 'transparent'; via = index of an earlier erase this fill re-uses) next to any older
     color replacements, so the same edits replay on the <=1024px preview AND the <=4000px source at Apply,
     persist in seedReplacements for re-editing, and undo/redo is a list of edits. */
  var SHAPE_TOL = 35;
  /* 4-connected flood fill. Transparent seed (alpha < 128): alpha <= 20 + 1.5·tol. Opaque seed: alpha >= 128
     within RGB distance tol·2.2 + 6 of the seed color. Each pixel is queued at most once. */
  function floodMask(data, W, H, sx, sy, tol) {
    sx = Math.max(0, Math.min(W - 1, sx | 0));
    sy = Math.max(0, Math.min(H - 1, sy | 0));
    var si = (sy * W + sx) * 4;
    var transparent = data[si + 3] < 128;
    var sr = data[si], sg = data[si + 1], sb = data[si + 2];
    var th = tol * 2.2 + 6, th2 = th * th, aMax = 20 + tol * 1.5;
    var n = W * H, mark = new Uint8Array(n), stack = new Int32Array(n), sp = 0, count = 0;
    var start = sy * W + sx;
    stack[sp++] = start; mark[start] = 3;
    while (sp) {
      var i = stack[--sp], p = i * 4, ok;
      if (transparent) ok = data[p + 3] <= aMax;
      else {
        var dr = data[p] - sr, dg = data[p + 1] - sg, db = data[p + 2] - sb;
        ok = data[p + 3] >= 128 && dr * dr + dg * dg + db * db <= th2;
      }
      if (!ok) { mark[i] = 2; continue; }
      mark[i] = 1; count++;
      var x = i % W;
      if (x > 0 && !mark[i - 1]) { mark[i - 1] = 3; stack[sp++] = i - 1; }
      if (x < W - 1 && !mark[i + 1]) { mark[i + 1] = 3; stack[sp++] = i + 1; }
      if (i >= W && !mark[i - W]) { mark[i - W] = 3; stack[sp++] = i - W; }
      if (i < n - W && !mark[i + W]) { mark[i + W] = 3; stack[sp++] = i + W; }
    }
    for (var k = 0; k < n; k++) if (mark[k] !== 1) mark[k] = 0;
    return { mask: mark, count: count, transparent: transparent, rgb: [sr, sg, sb] };
  }
  /* every opaque pixel within tol·2.2 + 6 of a color (a swatch tap) */
  function globalMask(data, W, H, rgb, tol) {
    var th = tol * 2.2 + 6, th2 = th * th, n = W * H, m = new Uint8Array(n), count = 0;
    for (var i = 0; i < n; i++) {
      var p = i * 4;
      if (data[p + 3] < 128) continue;
      var dr = data[p] - rgb[0], dg = data[p + 1] - rgb[1], db = data[p + 2] - rgb[2];
      if (dr * dr + dg * dg + db * db <= th2) { m[i] = 1; count++; }
    }
    return { mask: m, count: count, transparent: false, rgb: rgb };
  }
  /* the part a fill re-uses: an earlier erase's region, only where it is still transparent */
  function erasedMask(data, em) {
    var m = new Uint8Array(em.length), count = 0;
    for (var i = 0; i < em.length; i++) if (em[i] && data[i * 4 + 3] < 128) { m[i] = 1; count++; }
    return m;
  }
  /* Transparent removes; an empty-spot seed is FILLED (the pixel laid over the new color so soft edges blend);
     anything else is recolored flat (alpha kept). */
  function paintMask(data, m, seedT, target) {
    var rgb = target === 'transparent' ? null : hexToRgb(target);
    for (var i = 0, n = m.length; i < n; i++) {
      if (!m[i]) continue;
      var p = i * 4;
      if (!rgb) { data[p + 3] = 0; continue; }
      if (seedT) {
        var a = data[p + 3] / 255;
        data[p] = Math.round(data[p] * a + rgb[0] * (1 - a));
        data[p + 1] = Math.round(data[p + 1] * a + rgb[1] * (1 - a));
        data[p + 2] = Math.round(data[p + 2] * a + rgb[2] * (1 - a));
        data[p + 3] = 255;
      } else {
        data[p] = rgb[0]; data[p + 1] = rgb[1]; data[p + 2] = rgb[2];
      }
    }
  }
  /* the mask one edit covers on the image as it stands when that edit runs */
  function opMask(data, W, H, op, masks) {
    if (op.scope === 'all') return globalSel(data, W, H, hexToRgb(op.color), op.tol).mask;
    if (op.via != null && masks[op.via]) return erasedMask(data, masks[op.via]);
    return floodSel(data, W, H, Math.floor(op.fx * W), Math.floor(op.fy * H), op.tol).mask;
  }
  /* replays the edits in order; returns each edit's mask (refilling an erased part needs them) */
  function applyShapeOps(data, W, H, list, masks0) {
    var masks = masks0 ? masks0.slice() : [];
    (list || []).forEach(function (op) {
      if (!op || op.type !== 'shape') { masks.push(null); return; }
      /* recolor / erase of an opaque color or part: Owen's engine when it's loaded (see r2Paint).
         Filling an empty spot and refilling an erased part keep the flood + paint below. */
      if (R2 && op.target && !op.seedT && op.via == null) {
        var g = r2Paint(data, W, H, op);
        masks.push(g);
        if (op.target === 'transparent') dropRim(data, W, H, g);
        return;
      }
      var m = opMask(data, W, H, op, masks);
      masks.push(m);
      if (op.target) paintMask(data, m, !!op.seedT, op.target);
      if (op.target === 'transparent' && !op.seedT) dropRim(data, W, H, m);
    });
    return masks;
  }
  /* By Size v5 only (sandbox 11-01): making a color transparent left its faint anti-aliased rim (alpha 10-127) where it
     met the background — ~900 px around the QA canvas's white circle. That ring printed as a ghost outline and kept the
     trimmed print size at the full circle. Faint pixels touching what this edit erased go too (2 px). */
  function dropRim(data, W, H, m) {
    if (!window.__bsv5Tools || !m) return;
    var n = W * H, hit = new Uint8Array(n);
    for (var i = 0; i < n; i++) if (m[i]) hit[i] = 1;
    for (var pass = 0; pass < 2; pass++) {
      var next = [];
      for (var k = 0; k < n; k++) {
        var a = data[k * 4 + 3];
        if (hit[k] || !a || a >= 128) continue;
        var x = k % W;
        if (x > 0 && hit[k - 1] || x < W - 1 && hit[k + 1] || k >= W && hit[k - W] || k < n - W && hit[k + W]) next.push(k);
      }
      for (var j = 0; j < next.length; j++) { data[next[j] * 4 + 3] = 0; hit[next[j]] = 1; }
      if (!next.length) break;
    }
  }
  function shapeOnly(list) { return (list || []).filter(function (r) { return r && r.type === 'shape'; }); }
  function colorOnly(list) { return (list || []).filter(function (r) { return r && r.type !== 'shape'; }); }
  /* the marching-ants line: mask pixels on its edge plus the outside pixels touching them (2px) */
  function maskOutline(m, W, H) {
    var out = [], n = W * H, seen = new Uint8Array(n);
    for (var i = 0; i < n; i++) {
      if (!m[i]) continue;
      var x = i % W, edge = x === 0 || x === W - 1 || i < W || i >= n - W;
      var nb = [x > 0 ? i - 1 : -1, x < W - 1 ? i + 1 : -1, i >= W ? i - W : -1, i < n - W ? i + W : -1];
      for (var j = 0; j < 4; j++) {
        var q = nb[j];
        if (q < 0) continue;
        if (!m[q]) { edge = true; if (!seen[q]) { seen[q] = 1; out.push(q); } }
      }
      if (edge && !seen[i]) { seen[i] = 1; out.push(i); }
    }
    return out;
  }
  function rgbDist2(a, b) { var dr = a[0] - b[0], dg = a[1] - b[1], db = a[2] - b[2]; return dr * dr + dg * dg + db * db; }
  /* Step 1 colors: 512 bins (alpha >= 200), merge bins < 70 apart, keep up to 8 over 1% of the image, then any
     color the user applied that is still in the picture (so a new color always shows as a swatch). */
  /* ───────────── Owen's color engine (assets/nt-recolor2.js, NTRecolor2) ─────────────
     Justin 2026-10-30: keep this dialog, use Owen's color detection. When nt-recolor2.js loads, it
     decides WHICH pixels a swatch or a tapped part means and HOW they change:
       - colors compared in OKLab (how people see them), tolerance = a real perceptual radius, with
         the design's other colors competing so one color doesn't bleed into its neighbour;
       - Step 1 swatches = its weighted k-means palette (one shirt = one swatch);
       - photos recolor in 'shade' mode (keeps folds and texture), flat art in 'solid' mode (keeps
         anti-aliased edges exact); erase un-mixes the erased color out of edge pixels and gets the
         DTF print cleanup (no faint rims, no specks).
     Selections stay binary masks (weight > 0.5) so the marching ants, hover and the erase-refill
     bookkeeping work unchanged. If the file can't load, everything below falls back to v1. */
  var R2 = null, R2_AN = null, r2LutCache = new Map();
  /* Owen's AI tap-select (sam) is NOT used: Justin 10-30 wants our own selection tool unchanged, only
     the engine's color detection + rendering. A tap still selects the connected area of that color. */
  function loadR2() {
    if (window.NTRecolor2 && window.NTRecolor2.plan) { R2 = window.NTRecolor2; return Promise.resolve(R2); }
    var url = RCL_SRC ? RCL_SRC.replace(/studio-recolor(\.min)?\.js/, 'nt-recolor2.js') : '';
    if (!url) return Promise.resolve(null);
    if (!loadR2.p) {
      loadR2.p = new Promise(function (resolve) {
        var sc = document.createElement('script');
        sc.src = url; sc.async = true;
        sc.onload = function () { R2 = window.NTRecolor2 && window.NTRecolor2.plan ? window.NTRecolor2 : null; resolve(R2); };
        sc.onerror = function () { console.warn('[studio-recolor] nt-recolor2.js failed to load, using v1'); resolve(null); };
        document.head.appendChild(sc);
      });
    }
    /* never hold the dialog more than 6s for it */
    return Promise.race([loadR2.p, new Promise(function (r) { setTimeout(function () { r(R2); }, 6000); })]);
  }
  function r2Img(data, W, H) { return { data: data, width: W, height: H }; }
  /* photo vs flat art is decided ONCE from the untouched upload when the dialog opens and reused at
     Apply, so the preview and the full-size print file render the same way. The colors an edit must not
     bleed into ("competitors") come from the image AS IT STANDS before that edit: a color added by an
     earlier swap competes too (an erase of white otherwise took a yellow added a step before). */
  var r2AnCache = new Map();
  function r2An(data, W, H) {
    var first = !R2_AN;
    if (first) R2_AN = R2.analyze(r2Img(data, W, H));
    var h = W + 'x' + H, step = Math.max(4, ((data.length / 4) / 1500) | 0) * 4, acc = 0;
    for (var i = 0; i < data.length; i += step) acc = (acc * 31 + data[i] * 7 + data[i + 1] * 3 + data[i + 2] + data[i + 3] * 11) | 0;
    h += ':' + acc;
    var an = r2AnCache.get(h);
    if (!an) {
      /* By Size v5 (snappy tools 11-03): the first analysis already ran on this very picture, and analysing it again
         with its own kind gives the identical result (checked on flat + photo art) — reuse it (~70 ms off each open) */
      an = first && window.__bsv5Tools ? R2_AN : R2.analyze(r2Img(data, W, H), { kind: R2_AN.kind });
      an.key = h;
      if (r2AnCache.size > 12) r2AnCache.clear();
      r2AnCache.set(h, an);
    }
    return an;
  }
  function r2Mode(an) { return an.kind === 'photo' ? 'shade' : 'solid'; }
  function r2Lut(an, rgb, tol) {
    var k = rgb.join(',') + '|' + tol + '|' + an.kind + '|' + an.key;
    var lut = r2LutCache.get(k);
    if (!lut) {
      lut = R2.buildLut({ from: rgb, to: rgb, tol: tol, mode: r2Mode(an) }, { swatches: an.swatches, flatRuns: an.stats && an.stats.flatRuns });
      if (r2LutCache.size > 40) r2LutCache.clear();
      r2LutCache.set(k, lut);
    }
    return lut;
  }
  function r2Binary(img, layer) {   /* the engine's soft weight -> the dialog's 0/1 mask */
    var n = img.width * img.height, out = { data: new Uint8ClampedArray(n * 4), width: img.width, height: img.height };
    R2.selectionOverlay(img, layer, out);
    var m = new Uint8Array(n), count = 0;
    for (var i = 0; i < n; i++) if (out.data[i * 4 + 3] > 85) { m[i] = 1; count++; }
    return { mask: m, count: count };
  }
  function r2From(img, sx, sy) {
    var d = img.data, p = (sy * img.width + sx) * 4;
    return R2.sampleAt(img, sx, sy, 2) || [d[p], d[p + 1], d[p + 2]];
  }
  /* "this color everywhere" (a Step 1 swatch) */
  function globalSel(data, W, H, rgb, tol) {
    if (!R2) return globalMask(data, W, H, rgb, tol);
    var img = r2Img(data, W, H), b = r2Binary(img, { lut: r2Lut(r2An(data, W, H), rgb, tol), mask: null });
    return { mask: b.mask, count: b.count, transparent: false, rgb: rgb };
  }
  /* "only this part" (a tap): the connected area the engine's color test reaches from the tap.
     Empty (transparent) spots keep the v1 flood — that's the fill-it-in path. */
  function floodSel(data, W, H, sx, sy, tol) {
    sx = Math.max(0, Math.min(W - 1, sx | 0)); sy = Math.max(0, Math.min(H - 1, sy | 0));
    if (!R2 || data[(sy * W + sx) * 4 + 3] < 128) return floodMask(data, W, H, sx, sy, tol);
    var img = r2Img(data, W, H), rgb = r2From(img, sx, sy), an = r2An(data, W, H);
    var lut = r2Lut(an, rgb, tol);
    var rm = R2.regionMask(img, lut, sx, sy);
    if (!rm) return floodMask(data, W, H, sx, sy, tol);
    /* the region itself is the selection (one pass; the LUT-weighted overlay pass doubled hover cost) */
    var n = W * H, m = new Uint8Array(n), count = 0;
    for (var i = 0; i < n; i++) if (rm.m[i] > 0.5 && data[i * 4 + 3] >= 8) { m[i] = 1; count++; }
    return { mask: m, count: count, transparent: false, rgb: rgb };
  }
  /* one recolor / erase op through the engine; returns the 0/1 mask the dialog keeps for it (for an
     erase: the pixels it actually made transparent, so a later fill can re-use that part) */
  function r2Paint(data, W, H, op) {
    var img = r2Img(data, W, H), an = r2An(data, W, H), part = op.scope !== 'all';
    var sx = Math.max(0, Math.min(W - 1, Math.floor(op.fx * W))), sy = Math.max(0, Math.min(H - 1, Math.floor(op.fy * H)));
    var from = part ? r2From(img, sx, sy) : hexToRgb(op.color);
    var erase = op.target === 'transparent';
    var layer = R2.plan(img, an, { from: from, to: erase ? null : hexToRgb(op.target), tol: op.tol, mode: 'auto',
      seed: part ? { fx: op.fx, fy: op.fy } : null }, null);
    if (!part) layer.mask = null;
    else if (!layer.mask) layer.mask = R2.regionMask(img, layer.lut, sx, sy);
    if (part && !layer.mask) return new Uint8Array(W * H);   /* tap missed every qualifying pixel: no-op */
    var sel = r2Binary(img, layer).mask;
    var before = erase ? new Uint8ClampedArray(data) : null;
    R2.apply(img, [layer]);
    if (!erase) return sel;
    R2.finishErase(img, before);
    var gone = new Uint8Array(W * H);
    for (var i = 0, n = W * H; i < n; i++) if (before[i * 4 + 3] >= 128 && data[i * 4 + 3] < 128) gone[i] = 1;
    return gone;
  }
  /* Step 1 swatches from the engine's palette; colors the customer applied still get a swatch */
  /* The engine's swatches are cluster means: on photo-like art they drift a few levels after every
     edit, and each drift read as a new color (the Step 2 grid grew to 100+ near-copies, Justin 10-30).
     A color seen once keeps its hex for the whole dialog: a later swatch within ~20 levels reuses it. */
  var r2Seen = [];
  function r2Stable(rgb, hex) {
    for (var i = 0; i < r2Seen.length; i++) if (rgbDist2(r2Seen[i].rgb, rgb) < 20 * 20) return r2Seen[i];
    var e = { rgb: rgb.slice(), hex: hex.toUpperCase() };
    r2Seen.push(e);
    return e;
  }
  function paletteOf(img, added) {
    if (!R2) return extractPaletteV2(img, added);
    var res = [];
    (r2An(img.data, img.width, img.height).swatches || []).slice(0, 8).forEach(function (sw) {
      /* a shaded recolor's average lands near the color the customer picked: that IS the picked color */
      var mine = (added || []).filter(function (h) { return h !== 'transparent' && rgbDist2(hexToRgb(h), sw.rgb) < 36 * 36; })[0];
      var e = mine ? r2Stable(hexToRgb(mine), mine) : r2Stable(sw.rgb, sw.hex);
      if (!res.some(function (r) { return r.hex === e.hex; })) res.push({ rgb: e.rgb.slice(), hex: e.hex });
    });
    var a = img.data;
    (added || []).forEach(function (hex) {
      var c = hexToRgb(hex);
      if (res.some(function (r) { return r.hex === hex || rgbDist2(r.rgb, c) < 12 * 12; })) return;
      for (var q = 0; q < a.length; q += 4) {
        if (a[q + 3] >= 128 && a[q] === c[0] && a[q + 1] === c[1] && a[q + 2] === c[2]) { res.push({ rgb: c, hex: hex }); return; }
      }
    });
    return res;
  }
  function extractPaletteV2(img, added) {
    var a = img.data, total = img.width * img.height, buckets = new Map(), i;
    for (i = 0; i < a.length; i += 4) {
      if (a[i + 3] < 200) continue;
      var k = (a[i] >> 5) << 6 | (a[i + 1] >> 5) << 3 | (a[i + 2] >> 5);
      var b = buckets.get(k); if (!b) { b = [0, 0, 0, 0]; buckets.set(k, b); }
      b[0] += a[i]; b[1] += a[i + 1]; b[2] += a[i + 2]; b[3]++;
    }
    var list = [];
    buckets.forEach(function (b) { list.push({ rgb: [b[0] / b[3], b[1] / b[3], b[2] / b[3]], n: b[3] }); });
    list.sort(function (x, y) { return y.n - x.n; });
    var out = [];
    list.forEach(function (it) {
      for (var j = 0; j < out.length; j++) if (rgbDist2(out[j].rgb, it.rgb) < 70 * 70) { out[j].n += it.n; return; }
      out.push({ rgb: it.rgb, n: it.n });
    });
    var res = out.filter(function (o) { return o.n / total > 0.01; }).slice(0, 8).map(function (o) {
      var r = [Math.round(o.rgb[0]), Math.round(o.rgb[1]), Math.round(o.rgb[2])];
      return { rgb: r, hex: rgbToHex(r[0], r[1], r[2]).toUpperCase() };
    });
    (added || []).forEach(function (hex) {
      var c = hexToRgb(hex);
      if (res.some(function (r) { return r.hex === hex || rgbDist2(r.rgb, c) < 12 * 12; })) return;
      for (var q = 0; q < a.length; q += 4) {
        if (a[q + 3] >= 128 && a[q] === c[0] && a[q + 1] === c[1] && a[q + 2] === c[2]) { res.push({ rgb: c, hex: hex }); return; }
      }
    });
    return res;
  }

  /* ───────────────────────── swatch extraction ───────────────────────── */
  function extractSwatches(imageData) {
    var data = imageData.data;
    var px = [];
    var stride = Math.max(1, Math.floor((data.length / 4) / 220000)) * 4;
    for (var p = 0; p < data.length; p += stride) {
      if (data[p + 3] < 128) continue;
      px.push([data[p], data[p + 1], data[p + 2]]);
    }
    if (!px.length) return [];

    var boxes = [px];
    while (boxes.length < 16) {
      boxes.sort(function (a, b2) { return spread(b2) - spread(a); });
      var box = boxes.shift();
      if (!box || box.length < 32 || spread(box) < 12) { if (box) boxes.push(box); break; }
      var ch = widestChannel(box);
      box.sort(function (a, b2) { return a[ch] - b2[ch]; });
      var mid = box.length >> 1;
      boxes.push(box.slice(0, mid), box.slice(mid));
    }
    function widestChannel(box) {
      var mins = [255, 255, 255], maxs = [0, 0, 0];
      for (var i = 0; i < box.length; i++) for (var c = 0; c < 3; c++) {
        if (box[i][c] < mins[c]) mins[c] = box[i][c];
        if (box[i][c] > maxs[c]) maxs[c] = box[i][c];
      }
      var dr = maxs[0] - mins[0], dg = maxs[1] - mins[1], db = maxs[2] - mins[2];
      return dg >= dr && dg >= db ? 1 : (dr >= db ? 0 : 2);
    }
    function spread(box) {
      var mins = [255, 255, 255], maxs = [0, 0, 0];
      for (var i = 0; i < box.length; i++) for (var c = 0; c < 3; c++) {
        if (box[i][c] < mins[c]) mins[c] = box[i][c];
        if (box[i][c] > maxs[c]) maxs[c] = box[i][c];
      }
      return (maxs[0] - mins[0]) + (maxs[1] - mins[1]) + (maxs[2] - mins[2]);
    }

    var clusters = boxes.filter(function (b2) { return b2.length; }).map(function (box) {
      var counts = new Map();
      for (var i = 0; i < box.length; i++) {
        var k = (box[i][0] << 16) | (box[i][1] << 8) | box[i][2];
        counts.set(k, (counts.get(k) || 0) + 1);
      }
      var bestK = 0, bestN = -1;
      counts.forEach(function (nn, k) { if (nn > bestN) { bestN = nn; bestK = k; } });
      return { rgb: [(bestK >> 16) & 255, (bestK >> 8) & 255, bestK & 255], count: box.length };
    });

    clusters.forEach(function (c) { c.lab = rgbToLab(c.rgb[0], c.rgb[1], c.rgb[2]); });
    clusters.sort(function (a, b2) { return b2.count - a.count; });
    var merged = [];
    clusters.forEach(function (c) {
      for (var i = 0; i < merged.length; i++) {
        if (deltaE(c.lab, merged[i].lab) < MERGE_DE) { merged[i].count += c.count; return; }
      }
      merged.push(c);
    });

    var total = px.length;
    var out = merged.map(function (c) {
      return { hex: rgbToHex(c.rgb[0], c.rgb[1], c.rgb[2]), coveragePercent: Math.round((c.count / total) * 1000) / 10 };
    }).sort(function (a, b2) { return b2.coveragePercent - a.coveragePercent; });
    out = out.filter(function (s, i) { return i === 0 || s.coveragePercent >= MIN_COVERAGE; });
    return out.slice(0, MAX_SWATCHES);
  }

  /* ─────────────────────────── image helpers ─────────────────────────── */

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('image load failed')); };
      img.src = url;
    });
  }
  /* Canvas work needs CORS-readable pixels. Customer uploads live on the
     uploads bucket (mirror to its imgix host, CORS *); Ready-To-Press files
     live on s3.amazonaws.com/ninja-imgix (NO CORS) — use the rtp-mirror copy
     (see rtp-mirror-uploads-bucket memory / studio-prev-designs.js). */
  var S3_UPLOADS = 'ninja-services-production-ninjauploadss3bucket-zks2mguobhe4.s3.amazonaws.com';
  var IMGIX_UPLOADS = 'ninjauploads-production.imgix.net';
  function corsCandidates(url) {
    url = String(url || '');
    var out = [];
    if (url.indexOf(S3_UPLOADS) > -1) out.push(url.split(S3_UPLOADS).join(IMGIX_UPLOADS));
    if (url.indexOf('s3.amazonaws.com/ninja-imgix/') > -1) {
      var b = url.split('/').pop().split('?')[0];
      try { b = decodeURIComponent(b); } catch (_) {}
      b = b.replace(/\+/g, ' ').trim();
      /* carry the imgix PROCESSING params (bg-remove/upscale) onto the
         mirror twin — it's an imgix URL too, so they apply there; the
         S3 original's other query junk (presign etc.) must NOT come. */
      var keep = (url.match(/[?&](?:bg-remove(?:-model)?|upscale)=[^&]*/g) || [])
        .map(function (s) { return s.slice(1); }).join('&');
      out.push('https://' + IMGIX_UPLOADS + '/rtp-mirror/' + encodeURIComponent(b) + (keep ? '?' + keep : ''));
    }
    if (out.indexOf(url) === -1) out.push(url);
    return out.map(sizeVector);
  }
  /* A vector (svg / ai / eps / pdf) rendered by imgix with no size comes back at the file's tiny built-in
     size (an NBA logo SVG = 51x30 px): the preview was a speck and Apply saved a 51x30 recolor (Justin
     2026-10-29). Ask imgix for the full working size instead; fit=max keeps the aspect ratio. */
  function sizeVector(u) {
    var m = /^https:\/\/[^/]+\.imgix\.net\/[^?#]+\.(svg|ai|eps|pdf)(\?[^#]*)?$/i.exec(u);
    if (!m || /[?&](w|h)=/.test(u)) return u;
    return u + (u.indexOf('?') > -1 ? '&' : '?') + 'w=' + MAX_SOURCE + '&h=' + MAX_SOURCE + '&fit=max';
  }
  function loadImageAny(urls) {
    var p = Promise.reject();
    urls.forEach(function (u) {
      p = p.catch(function () {
        return loadImage(u).then(function (img) { return { img: img, url: u }; });
      });
    });
    return p;
  }
  function drawScaled(img, maxDim) {
    var w = img.naturalWidth, h = img.naturalHeight;
    var scale = Math.min(1, maxDim / Math.max(w, h));
    var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
    var cv = document.createElement('canvas');
    cv.width = cw; cv.height = ch;
    cv.getContext('2d').drawImage(img, 0, 0, cw, ch);
    return cv;
  }
  function canvasToPngBlob(cv) {
    return new Promise(function (resolve, reject) {
      cv.toBlob(function (blob) { blob ? resolve(blob) : reject(new Error('encode failed')); }, 'image/png');
    });
  }
  /* PNG blob -> hosted https url via the studio presign pipeline. Shared by
     Recolor's apply and the local Image Filters apply on the legacy PDPs
     (fxPush -> processFileOrUrl wants an https S3/imgix url, not a blob:). */
  function hostPngBlob(blob, prefix) {
    if (typeof window.getPresignedUploadUrl !== 'function') return Promise.reject(new Error('getPresignedUploadUrl unavailable'));
    var fname = (prefix || 'recolor') + '____image_' + Date.now() + '_' + Math.floor(Math.random() * 1000) + '.png';
    return window.getPresignedUploadUrl(fname, 'image/png').then(function (presign) {
      if (!presign || !presign.url || !presign.sourceUrl) throw new Error('presign failed');
      return fetch(presign.url, { method: 'PUT', headers: { 'Content-Type': 'image/png' }, body: blob })
        .then(function (res) { if (!res.ok) throw new Error('PUT ' + res.status); return presign.sourceUrl; });
    });
  }
  /* full-res process -> hosted PNG url via the studio presign pipeline */
  /* the full-size render. By Size v5 with the colour engine loaded: in the background worker (the page keeps running;
     same code, same pixels). Anything else, or any worker failure: on the page, exactly as before. */
  var RW = { w: null, dead: false, seq: 0, jobs: {} };
  function recolorWorker() {
    if (RW.w || RW.dead) return RW.w;
    if (typeof Worker === 'undefined' || typeof OffscreenCanvas === 'undefined' || typeof createImageBitmap === 'undefined' || !/^https?:/.test(RCL_SRC)) { RW.dead = true; return null; }
    try {
      var eng = RCL_SRC.replace(/studio-recolor(\.min)?\.js/, 'nt-recolor2.js');
      var boot = 'self.window = self; importScripts(' + JSON.stringify(eng) + ', ' + JSON.stringify(RCL_SRC) + ');';
      var w = new Worker(URL.createObjectURL(new Blob([boot], { type: 'text/javascript' })));
      w.onmessage = function (e) {
        var d = e.data || {}, j = RW.jobs[d.id];
        if (!j) return;
        delete RW.jobs[d.id]; clearTimeout(j.t);
        if (d.kind && !R2_AN) R2_AN = { kind: d.kind };   /* as the page render would have left it */
        if (d.blob) j.ok(d.blob); else j.no(new Error(d.error || 'worker render failed'));
      };
      w.onerror = function () {
        RW.dead = true; RW.w = null;
        Object.keys(RW.jobs).forEach(function (k) { clearTimeout(RW.jobs[k].t); RW.jobs[k].no(new Error('worker error')); });
        RW.jobs = {};
        try { w.terminate(); } catch (_) {}
      };
      RW.w = w;
    } catch (e) { RW.dead = true; }
    return RW.w;
  }
  function processToBlob(baseSrc, replacements, palette) {
    var w = !IN_WORKER && window.__bsv5Tools && R2 && !window.__rclNoWorker ? recolorWorker() : null;
    if (!w) return processToBlobHere(baseSrc, replacements, palette);
    return new Promise(function (ok, no) {
      var id = ++RW.seq;
      RW.jobs[id] = { ok: ok, no: no, t: setTimeout(function () { delete RW.jobs[id]; no(new Error('worker timeout')); }, 90000) };
      w.postMessage({ id: id, src: baseSrc, repl: replacements, palette: palette, kind: R2_AN ? R2_AN.kind : null, v5: !!window.__bsv5Tools });
    }).catch(function (err) {
      console.warn('[studio-recolor] background render failed, rendering on the page', err);
      return processToBlobHere(baseSrc, replacements, palette);
    });
  }
  /* inside the worker: the same steps as processToBlobHere (decode, fit to MAX_SOURCE, recolour, PNG) without a page */
  function workerRender(d) {
    if (d.kind) R2_AN = { kind: d.kind };
    var urls = corsCandidates(d.src), p = Promise.reject();
    urls.forEach(function (u) {
      p = p.catch(function () {
        return fetch(u, { mode: 'cors' }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.blob(); })
          .then(function (b) { return createImageBitmap(b); });
      });
    });
    return p.then(function (bm) {
      var w = bm.width, h = bm.height, scale = Math.min(1, MAX_SOURCE / Math.max(w, h));
      var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
      var cv = new OffscreenCanvas(cw, ch), ctx = cv.getContext('2d');
      ctx.drawImage(bm, 0, 0, cw, ch);
      var full = ctx.getImageData(0, 0, cw, ch);
      applyColorReplacements(full.data, d.repl, d.palette);
      applyShapeOps(full.data, cw, ch, shapeOnly(d.repl));
      ctx.putImageData(full, 0, 0);
      return cv.convertToBlob({ type: 'image/png' });
    });
  }
  function processToBlobHere(baseSrc, replacements, palette) {
    return loadImage(baseSrc).then(function (img) {
      var cv = drawScaled(img, MAX_SOURCE);
      var ctx = cv.getContext('2d');
      var full = ctx.getImageData(0, 0, cv.width, cv.height);
      applyColorReplacements(full.data, replacements, palette);
      applyShapeOps(full.data, cv.width, cv.height, shapeOnly(replacements));
      ctx.putImageData(full, 0, 0);
      return canvasToPngBlob(cv);
    });
  }
  function processAndHost(baseSrc, replacements, palette) {
    return processToBlob(baseSrc, replacements, palette).then(function (blob) { return hostPngBlob(blob, 'recolor'); });
  }

  /* ─────────────────────────────── UI ─────────────────────────────── */

  /* the v5 tool bar's curved Undo / Redo arrows (shared with the Eraser via window.__ntBarIcons) */
  var NTBAR_UNDO = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/></svg>';
  var NTBAR_REDO = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m15 14 5-5-5-5"/><path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13"/></svg>';
  if (typeof window !== 'undefined') window.__ntBarIcons = { undo: NTBAR_UNDO, redo: NTBAR_REDO };
  var CSS = '' +
    '.rcl-ov{position:fixed;inset:0;z-index:100000;background:rgba(15,23,42,.55);display:flex;align-items:center;justify-content:center;padding:16px;}' +
    '.rcl{background:#fff;border-radius:16px;width:min(860px,100%);max-height:min(92vh,760px);display:flex;flex-direction:column;box-shadow:0 24px 64px rgba(15,23,42,.35);font-family:"DM Sans",system-ui,sans-serif;color:#0f172a;}' +
    '.rcl__head{display:flex;align-items:center;justify-content:space-between;padding:16px 20px 10px;}' +
    '.rcl__title{font-size:18px;font-weight:700;margin:0;}' +
    '.rcl__close{appearance:none;border:0;background:transparent;cursor:pointer;padding:6px;line-height:0;color:#0f172a;}' +
    '.rcl__body{display:flex;gap:18px;padding:0 20px 16px;min-height:0;flex:1 1 auto;overflow:auto;overscroll-behavior:contain;}' +
    /* the image column centres vertically against the (taller) controls column (Justin 2026-09-08) */
    '.rcl__left{flex:1 1 52%;min-width:0;display:flex;flex-direction:column;justify-content:center;gap:10px;}' +
    '.rcl__right{flex:1 1 48%;min-width:0;display:flex;flex-direction:column;gap:12px;}' +
    '.rcl__stage{position:relative;border:1px solid #e5e7eb;border-radius:12px;overflow:hidden;background:#f2f2f2 url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbD0iI0M1QzVDNSIgZD0iTTAgMGg0djRIMHpNMCA4aDR2NEgwek0xNiA4aC00VjRoNHpNMTYgMTZoLTR2LTRoNHpNOCAwaDR2NEg4ek04IDhoNHY0SDh6TTggOEg0VjRoNHpNOCAxNkg0di00aDR6Ii8+PHBhdGggZmlsbD0iIzlCOUI5QiIgZD0iTTQgMGg0djRINHpNNCA4aDR2NEg0ek0xMiA4SDhWNGg0ek0xMiAxNkg4di00aDR6TTEyIDBoNHY0aC00ek0xMiA4aDR2NGgtNHpNNCA4SDBWNGg0ek00IDE2SDB2LTRoNHoiLz48L3N2Zz4=) center center;display:flex;align-items:center;justify-content:center;min-height:220px;}' +
    /* fills the frame even for a small image (scaled to fit, never stretched) — Justin 2026-10-29 */
    '.rcl__stage canvas{width:100%;height:auto;max-width:100%;max-height:46vh;object-fit:contain;display:block;}' +
    '.rcl__compare{align-self:center;appearance:none;border:1px solid #e5e7eb;background:#fff;border-radius:8px;padding:7px 14px;font:600 12.5px/1 "DM Sans",system-ui,sans-serif;color:#374151;cursor:pointer;}' +
    '.rcl__compare:active{background:#f3f4f6;}' +
    /* By Size v5: Cropped / Full image preview toggle (shown only for a framed design) */
    '.rcl__view{display:inline-flex;align-self:center;gap:2px;padding:3px;border-radius:9px;background:#f1f3f5;margin:0;}' +
    '.rcl__view button{appearance:none;border:0;background:none;border-radius:7px;padding:6px 12px;font:600 12.5px/1 "DM Sans",system-ui,sans-serif;color:#4b5563;cursor:pointer;}' +
    '.rcl__view button.is-on{background:#fff;color:#019AFF;box-shadow:0 1px 2px rgba(23,24,26,.12);}' +
    '.fx .rcl__view{align-self:flex-start;margin:0 20px 12px;}' +
    '.fx__grid.fx--view .fx__thumb img,.fx__grid.fx--view .fx__thumb canvas{left:var(--fxl);top:var(--fxt);width:var(--fxw);height:var(--fxh);object-fit:fill;}' +
    '.rcl__sect{font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#6b7280;margin:0;}' +
    /* row gap must clear the % label hanging ~16px below each swatch — 8px let
       a second swatch row overwrite the first row's % labels (Justin 7-14) */
    /* same 10-up grid as the palette so a design swatch is exactly a palette swatch
       (Justin 2026-09-08 "pick a color icons should be same size as replace it with") */
    '.rcl__sw{display:grid;grid-template-columns:repeat(10,1fr);gap:22px 6px;padding-bottom:18px;}' +
    '.rcl__swatch{appearance:none;border:1.5px solid #e5e7eb;border-radius:6px;width:auto;height:auto;aspect-ratio:1;cursor:pointer;position:relative;padding:0;}' +
    '.rcl__swatch.is-sel{border-color:#019AFF;box-shadow:0 0 0 2px rgba(1,154,255,.25);}' +
    '.rcl__swatch .pct{position:absolute;left:0;right:0;bottom:-16px;font-size:9.5px;color:#6b7280;text-align:center;}' +
    '.rcl__swatch .dot{position:absolute;top:-5px;right:-5px;width:16px;height:16px;border-radius:50%;border:2px solid #fff;display:none;box-shadow:0 1px 3px rgba(0,0,0,.3);}' +
    '.rcl__swatch.has-repl .dot{display:block;}' +
    '.rcl__pal{display:grid;grid-template-columns:repeat(10,1fr);gap:6px;}' +
    /* "Replace it with" has two sections (Justin 2026-09-08): the design's own colors, then More colors */
    '.rcl__subsect{font-size:11px;font-weight:600;color:#6b7280;margin:-4px 0 -6px;}' +
    '.rcl__own:empty{display:none;}' +
    /* step 2 waits for a step-1 pick (Justin 2026-09-09) */
    '.rcl-ov:not(.has-pick) .rcl__step2{display:none;}' + /* the class rides state.ov (.rcl-ov), the dialog is .rcl inside it */
    '.rcl__step2{display:flex;flex-direction:column;gap:12px;}' +
    '.rcl__pcol{appearance:none;border:1.5px solid #e5e7eb;border-radius:6px;aspect-ratio:1;cursor:pointer;padding:0;}' +
    '.rcl__pcol.is-sel{border-color:#019AFF;box-shadow:0 0 0 2px rgba(1,154,255,.3);}' +
    '.rcl__rowline{display:flex;align-items:center;gap:10px;flex-wrap:wrap;}' +
    '.rcl__ghost{appearance:none;border:1px solid #e5e7eb;background:#fff;border-radius:8px;padding:8px 12px;font:600 12.5px/1 "DM Sans",system-ui,sans-serif;color:#374151;cursor:pointer;display:inline-flex;align-items:center;gap:7px;}' +
    '.rcl__ghost.is-sel{border-color:#019AFF;color:#019AFF;box-shadow:0 0 0 2px rgba(1,154,255,.2);}' +
    '.rcl__checker{width:15px;height:15px;border-radius:4px;border:1px solid #d1d5db;background:#f2f2f2 url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbD0iI0M1QzVDNSIgZD0iTTAgMGg0djRIMHpNMCA4aDR2NEgwek0xNiA4aC00VjRoNHpNMTYgMTZoLTR2LTRoNHpNOCAwaDR2NEg4ek04IDhoNHY0SDh6TTggOEg0VjRoNHpNOCAxNkg0di00aDR6Ii8+PHBhdGggZmlsbD0iIzlCOUI5QiIgZD0iTTQgMGg0djRINHpNNCA4aDR2NEg0ek0xMiA4SDhWNGg0ek0xMiAxNkg4di00aDR6TTEyIDBoNHY0aC00ek0xMiA4aDR2NGgtNHpNNCA4SDBWNGg0ek00IDE2SDB2LTRoNHoiLz48L3N2Zz4=) center center;background-size:12px 12px;}' +
    /* Custom = round color-wheel button; the native color input is stretched
       invisibly across it so a tap anywhere opens the OS picker (Justin 7-10). */
    '.rcl__customwrap{position:relative;cursor:pointer;}' +
    '.rcl__wheel{display:block;width:15px;height:15px;border-radius:50%;background:conic-gradient(#f00,#ff8000,#ffe600,#7dd400,#00c853,#00e5ff,#0091ff,#2400ff,#8000ff,#e100ff,#ff0080,#f00);box-shadow:inset 0 0 0 1px rgba(0,0,0,.08);}' +
    '.rcl__custom{position:absolute;inset:0;width:100%;height:100%;opacity:0;padding:0;border:0;cursor:pointer;}' +
    '.rcl__tol{display:flex;align-items:center;gap:10px;}' +
    /* theme.css styles EVERY input (appearance:none, 1px border, 7px radius,
       12px padding, 42px tall, white bg) — a range renders as a bordered box
       with a floating knob. Reset it back to the native track (staging
       screenshot 9-11, the PDP Intensity row). */
    '.rcl__tol input[type=range]{flex:1 1 auto;accent-color:#019AFF;-webkit-appearance:auto;appearance:auto;height:auto;min-height:0;padding:0;margin:0;border:0;border-radius:0;background:transparent;box-shadow:none;width:auto;line-height:normal;}' +
    '.rcl__tol .val{font-size:12px;color:#6b7280;min-width:64px;text-align:right;}' +
    '.rcl__hint{font-size:12.5px;color:#6b7280;margin:0;}' +
    '.rcl__foot{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:12px 20px 16px;border-top:1px solid #f1f5f9;}' +
    '.rcl__btn{appearance:none;border:0;border-radius:10px;padding:11px 18px;font:700 14px/1 "DM Sans",system-ui,sans-serif;cursor:pointer;white-space:nowrap;}' +
    '.rcl__lbl--short{display:none;}' +
    '.rcl__btn--primary{background:linear-gradient(95deg,#22b6ff,#2563eb);color:#fff;}' +
    '.rcl__btn--primary:disabled{opacity:.5;cursor:default;}' +
    '.rcl__btn--ghost{background:#fff;border:1px solid #e5e7eb;color:#374151;font-weight:600;}' +
    '.rcl__hbtn{appearance:none;border:1px solid #e5e7eb;background:#fff;color:#374151;border-radius:50%;width:38px;height:38px;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;padding:0;}' +
    '.rcl__hbtn:hover:not(:disabled){border-color:#019AFF;color:#019AFF;}' +
    '.rcl__hbtn:disabled{opacity:.35;cursor:default;}' +
    '.rcl__busy{position:absolute;inset:0;background:rgba(255,255,255,.85);display:flex;flex-direction:column;gap:12px;align-items:center;justify-content:center;border-radius:12px;}' +
    '.rcl__spin{width:38px;height:38px;border-radius:50%;border:4px solid #d9ecff;border-top-color:#019AFF;animation:rclSpin .8s linear infinite;}' +
    '@keyframes rclSpin{to{transform:rotate(360deg);}}' +
    '.fx{position:relative;width:min(950px,100%);max-width:950px;}' +
    '.fx__hint{font-size:13px;color:#6b7280;margin:0;padding:0 20px 12px;}' +
    /* 5 tiles per line on desktop (Justin 7-14); tighten to 4 on mid widths so
       tiles never fall below ~110px, phones keep the 2-up layout below */
    '.fx__grid{display:grid;grid-template-columns:repeat(5,1fr);gap:12px;padding:0 20px 16px;flex:1 1 auto;overflow-y:auto;min-height:0;overscroll-behavior:contain;-webkit-overflow-scrolling:touch;}' +
    '.fx__card{appearance:none;background:#fff;border:1.5px solid #e5e7eb;border-radius:14px;padding:8px 8px 6px;cursor:pointer;display:flex;flex-direction:column;gap:6px;}' +
    '.fx__card.is-sel{border-color:#019AFF;box-shadow:0 0 0 2px rgba(1,154,255,.25);}' +
    '.fx__card.is-sel .fx__label{color:#019AFF;}' +
    /* position:relative + absolute img: WebKit inflates a flex-column item
       whose aspect-ratio box holds a height:100% img (extra dead space under
       the label on Safari, Owen 2026-07-17); pulling the img out of intrinsic
       sizing lets aspect-ratio alone set the height in every engine. */
    '.fx__thumb{display:block;position:relative;border-radius:10px;overflow:hidden;aspect-ratio:1;background:#f2f2f2 url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbD0iI0M1QzVDNSIgZD0iTTAgMGg0djRIMHpNMCA4aDR2NEgwek0xNiA4aC00VjRoNHpNMTYgMTZoLTR2LTRoNHpNOCAwaDR2NEg4ek04IDhoNHY0SDh6TTggOEg0VjRoNHpNOCAxNkg0di00aDR6Ii8+PHBhdGggZmlsbD0iIzlCOUI5QiIgZD0iTTQgMGg0djRINHpNNCA4aDR2NEg0ek0xMiA4SDhWNGg0ek0xMiAxNkg4di00aDR6TTEyIDBoNHY0aC00ek0xMiA4aDR2NGgtNHpNNCA4SDBWNGg0ek00IDE2SDB2LTRoNHoiLz48L3N2Zz4=) center center;}' +
    '.fx__thumb img,.fx__thumb canvas{position:absolute;top:0;left:0;display:block;width:100%;height:100%;object-fit:contain;}' +
    '.fx__label{font-size:13px;font-weight:700;color:#2B2B2B;text-align:center;line-height:1.2;}' +
    /* Intensity row (Owen 9-11 "just the simple path on pdp"): the Recolor
       .rcl__tol slider row, spanning the grid directly under the picked tile's row */
    '.fx__ctrls{grid-column:1/-1;padding:2px 6px 4px;}' +
    '.fx__ctrls-lab{font-size:13px;font-weight:700;color:#2B2B2B;white-space:nowrap;}' +
    '.fx__ctrls .val{min-width:44px;font-size:13px;font-weight:600;color:#2B2B2B;}' +
    '@media (max-width:900px){.fx__grid{grid-template-columns:repeat(4,1fr);}}' +
    '@media (max-width:700px){.fx__grid{grid-template-columns:repeat(2,1fr);gap:10px;}}' +
    /* ── Recolor v2 (Claude Design handoff, Justin 2026-10-29): one flow, part or color ── */
    '.rcl{width:min(1120px,100%);max-height:min(94vh,860px);}' +
    '.rcl__body{gap:32px;}' +
    '.rcl__left{flex:1 1 380px;max-width:520px;justify-content:flex-start;}' +
    '.rcl__right{flex:1.4 1 440px;gap:24px;}' +
    '.rcl__stage{position:relative;display:flex;align-items:center;justify-content:center;padding:40px 40px 64px;border-radius:16px;background-size:18px 18px;}' +
    '.rcl__frame{position:relative;line-height:0;cursor:crosshair;max-width:100%;}' +
    '.rcl__stage .rcl__frame canvas{display:block;width:100%;height:auto;max-width:none;max-height:none;object-fit:fill;}' +
    '.rcl__stage .rcl__frame canvas.rcl__ovl{position:absolute;inset:0;height:100%;pointer-events:none;}' +
    '.rcl__taphint{position:absolute;left:50%;bottom:14px;transform:translateX(-50%);display:flex;align-items:center;gap:8px;max-width:calc(100% - 28px);background:#fff;border:1px solid #e4e4e6;border-radius:999px;padding:8px 14px;box-shadow:0 4px 12px rgba(0,0,0,.12);color:#019AFF;pointer-events:none;}' +
    '.rcl__taphint span{font:600 13px/1.25 "DM Sans",system-ui,sans-serif;color:#2b2b2b;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
    '.rcl__taphint[hidden],.rcl__chip[hidden]{display:none;}' +
    '.rcl__chip{position:absolute;left:14px;bottom:14px;display:flex;align-items:center;gap:8px;max-width:calc(100% - 28px);background:rgba(24,24,25,.88);color:#fff;border-radius:999px;padding:5px 12px 5px 5px;font:500 13px/1.25 "DM Sans",system-ui,sans-serif;pointer-events:none;white-space:nowrap;}' +
    '.rcl__chipsw{flex:0 0 auto;width:22px;height:22px;border-radius:50%;border:1.5px solid rgba(255,255,255,.75);}' +
    '.rcl__isck{background:#f2f2f2 url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYiIGhlaWdodD0iMTYiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbD0iI0M1QzVDNSIgZD0iTTAgMGg0djRIMHpNMCA4aDR2NEgwek0xNiA4aC00VjRoNHpNMTYgMTZoLTR2LTRoNHpNOCAwaDR2NEg4ek04IDhoNHY0SDh6TTggOEg0VjRoNHpNOCAxNkg0di00aDR6Ii8+PHBhdGggZmlsbD0iIzlCOUI5QiIgZD0iTTQgMGg0djRINHpNNCA4aDR2NEg0ek0xMiA4SDhWNGg0ek0xMiAxNkg4di00aDR6TTEyIDBoNHY0aC00ek0xMiA4aDR2NGgtNHpNNCA4SDBWNGg0ek00IDE2SDB2LTRoNHoiLz48L3N2Zz4=) center center !important;background-size:10px 10px !important;}' +
    '.rcl__step{display:flex;align-items:center;gap:10px;}' +
    '.rcl__num{flex:0 0 auto;width:24px;height:24px;border-radius:50%;background:#019AFF;color:#fff;display:inline-flex;align-items:center;justify-content:center;font:800 13px/1 "DM Sans",system-ui,sans-serif;}' +
    '.rcl__step h2{margin:0;font:800 16.5px/1.25 "DM Sans",system-ui,sans-serif;color:#2b2b2b;letter-spacing:-.01em;}' +
    '.rcl__head--v2 .rcl__title{font-size:30px;font-weight:800;letter-spacing:-.01em;color:#0f1729;line-height:1.15;}' +
    '.rcl__s1{display:flex;flex-direction:column;gap:14px;}' +
    '.rcl__head--v2{align-items:flex-start;gap:24px;}' +
    '.rcl__headtx{display:flex;flex-direction:column;gap:8px;min-width:0;}' +
    '.rcl__desc{margin:0;max-width:760px;font-size:17px;line-height:1.55;color:#5a5b60;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
    '.rcl__sw{display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:6px;padding:0;}' +
    '.rcl__sw1{appearance:none;width:100%;aspect-ratio:1;border-radius:8px;border:1px solid rgba(0,0,0,.14);padding:0;cursor:pointer;box-sizing:border-box;}' +
    '.rcl__legendrow{display:flex;justify-content:flex-end;margin-bottom:-8px;}' +
    '.rcl__legend{display:inline-flex;align-items:center;gap:6px;font-size:13px;color:#717278;}' +
    '.rcl__dot{width:10px;height:10px;border-radius:999px;background:#2b2b2b;border:2px solid #fff;box-shadow:0 0 0 1px #cdced1;box-sizing:border-box;display:inline-block;flex:0 0 auto;}' +
    '.rcl__grid .rcl__pc .rcl__dot{position:absolute;top:3px;right:3px;}' +
    '.rcl__sw1.is-sel,.rcl__pc.is-sel{box-shadow:0 0 0 2px #fff,0 0 0 4px #019AFF;}' +
    '.rcl__step2,.rcl__tolblk{display:none;}' +
    '.rcl-ov.has-sel .rcl__step2,.rcl-ov.has-sel .rcl__tolblk{display:flex;flex-direction:column;}' +
    '.rcl__step2{gap:14px;}' +
    '.rcl__lab{margin:0;font:600 14px/1.2 "DM Sans",system-ui,sans-serif;color:#717278;}' +
    '.rcl__pc{appearance:none;position:relative;border-radius:8px;border:1px solid rgba(0,0,0,.14);padding:0;cursor:pointer;box-sizing:border-box;}' +
    '.rcl__grid{display:grid;grid-template-columns:repeat(12,minmax(0,1fr));gap:6px;}' +
    '.rcl__grid .rcl__pc{aspect-ratio:1;width:100%;}' +
    '.rcl__grid .rcl__pc--none{grid-column:span 3;aspect-ratio:auto;display:flex;align-items:center;justify-content:center;gap:6px;background:#fff;border-color:#cdced1;font:600 13px/1 "DM Sans",system-ui,sans-serif;color:#2b2b2b;white-space:nowrap;overflow:hidden;padding:0 6px;}' +
    '.rcl__tsq{flex:0 0 auto;width:16px;height:16px;border-radius:4px;border:1px solid rgba(0,0,0,.14);}' +
    '.rcl__pc--custom{background:conic-gradient(#f00,#ff8000,#ffe600,#7dd400,#00c853,#00e5ff,#0091ff,#2400ff,#8000ff,#e100ff,#ff0080,#f00);overflow:hidden;}' +
    '.rcl .rcl__pc--custom input{position:absolute;inset:0;width:100%;height:100%;opacity:0;padding:0;margin:0;border:0;cursor:pointer;}' +
    '.rcl__hint2{font-size:14px;line-height:1.5;color:#717278;margin:0;}' +
    '.rcl__tolblk{gap:8px;}' +
    '.rcl__tollab{margin:0;font:700 14px/1 "DM Sans",system-ui,sans-serif;letter-spacing:.12em;text-transform:uppercase;color:#717278;}' +
    '.rcl .rcl__tolblk input[type=range]{-webkit-appearance:auto;appearance:auto;accent-color:#019AFF;width:100%;height:auto;min-height:0;padding:0;margin:0;border:0;border-radius:0;background:transparent;box-shadow:none;line-height:normal;}' +
    '.rcl__tolends{display:flex;justify-content:space-between;font-size:13px;color:#717278;}' +
    '@media (max-width:700px){.rcl__left{max-width:none;}.rcl__stage{padding:24px 24px 56px;}.rcl__step h2{font-size:13px;}.rcl__num{width:21px;height:21px;font-size:11px;}.rcl__grid{gap:4px;}.rcl__grid .rcl__pc--none span:last-child{font-size:11px;}.rcl__taphint span{font-size:12px;}.rcl__chip{font-size:12px;}}' +
    '@media (max-width:700px){.rcl__body{flex-direction:column;}.rcl__stage canvas{max-height:34vh;}.rcl__pal{grid-template-columns:repeat(8,1fr);}' +
    /* phone fixes (Justin 7-10): sheet sat too low — iOS toolbars make 92vh taller
       than the visible area, clipping the bottom corners — lift 10px + cap height
       to the DYNAMIC viewport; design swatches shrink to the palette's ~40px so
       more fit per row; row-gap clears the % labels the old 8px gap let the
       second row cover. */
    '.rcl{transform:translateY(-10px);max-height:calc(100dvh - 42px);}' +
    '.rcl__sw{grid-template-columns:repeat(8,1fr);gap:20px 6px;padding-bottom:14px;}' +
    '.rcl__swatch .pct{bottom:-14px;font-size:9px;}' +
    /* footer fits phones: Apply was clipping bottom-right (Justin 7-10). Equal side
       padding, 13px labels on all three buttons, mobile-only 'Reset' label. */
    '.rcl__foot{padding:12px 14px 16px;gap:8px;}' +
    '.rcl__foot>div{gap:6px !important;}' +
    '.rcl__btn{padding:10px 12px;font-size:13px;}' +
    '.rcl__hbtn{width:34px;height:34px;flex:0 0 auto;}' +
    '.rcl__lbl--full{display:none;}.rcl__lbl--short{display:inline;}}' +
    /* By Size v5 tool bar (Owen 11-04): ONE bottom bar for Swap Colors + Eraser — [Undo] [Redo] [Reset] ... [Cancel]
       [Apply/Done]. Only footers carrying .ntbar get it (v5 dialogs); every other page keeps the bar above. Phone: Undo /
       Redo go icon-only squares. */
    /* ...and the header (Owen 11-04): Swap Colors' title + description sized like the Eraser's (18px title, the 12.5px
       grey "Drag over any part..." text), the description wrapping instead of cutting off */
    '.rcl__head--v2.rcl__head--v5 .rcl__title{font-size:18px;font-weight:700;letter-spacing:0;line-height:34px;color:inherit;}' +   /* 34px = the close button: title sits where the Eraser's (centred on the X) */
    /* title + X on one row, the description full width under both (Owen 11-04: it wrapped short of the X's column) */
    '.rcl__head--v2.rcl__head--v5{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:0 16px;align-items:start;}' +
    '.rcl__head--v2.rcl__head--v5 .rcl__headtx{display:contents;}' +
    '.rcl__head--v2.rcl__head--v5 .rcl__title{grid-column:1;grid-row:1;}' +
    '.rcl__head--v2.rcl__head--v5 .rcl__close{grid-column:2;grid-row:1;}' +
    '.rcl__head--v2.rcl__head--v5 .rcl__desc{grid-column:1/-1;grid-row:2;max-width:none;}' +
    '.rcl__head--v2.rcl__head--v5 .rcl__desc{font:400 12.5px/1.5 Inter,-apple-system,system-ui,sans-serif;color:#6b7280;white-space:normal;overflow:visible;}' +
    /* ...and the steps (Owen 11-04): 9 bigger swatches a row on both steps, Transparent 4 wide with its square on the left,
       the hint under the heading and the "Already in your image" legend under the colors, left */
    /* as many columns as fit, each stretched to fill the row: a row always ends at the edge (Owen 11-04), swatches
       44px to ~48px (phone 40 to ~46) */
    '.rcl__right--v5 .rcl__sw,.rcl__right--v5 .rcl__grid{grid-template-columns:repeat(auto-fill,minmax(44px,1fr));gap:8px;padding:0;}' +
    '.rcl__right--v5 .rcl__sw1,.rcl__right--v5 .rcl__pc{border-radius:10px;}' +
    '.rcl__right--v5 .rcl__grid .rcl__pc--none{grid-column:span 4;justify-content:flex-start;gap:10px;padding:0 10px;font-size:14px;}' +
    '.rcl__right--v5 .rcl__tsq{width:26px;height:26px;border-radius:6px;}' +
    '.rcl__right--v5 .rcl__legendrow{justify-content:flex-start;margin:0;}' +
    '.rcl__right--v5 .rcl__step h2{font-weight:600;}' +   /* step headings lighter than the old 800 (Owen 11-04) */
    '@media (max-width:700px){.rcl__right--v5 .rcl__sw,.rcl__right--v5 .rcl__grid{grid-template-columns:repeat(auto-fill,minmax(40px,1fr));gap:6px;}.rcl__right--v5 .rcl__sw1,.rcl__right--v5 .rcl__pc{border-radius:8px;}.rcl__right--v5 .rcl__grid .rcl__pc--none{gap:7px;padding:0 6px;}.rcl__right--v5 .rcl__grid .rcl__pc--none span:last-child{font-size:12px;}.rcl__right--v5 .rcl__tsq{width:18px;height:18px;border-radius:5px;}.rcl__right--v5 .rcl__hint2{font-size:13px;}}' +
    '.rcl__foot.ntbar{padding:12px 20px 16px;gap:10px;}' +
    '.ntbar [hidden]{display:none !important;}' +
    '.ntbar>div{gap:8px !important;align-items:center;}' +
    '.ntbar .ntbar__h,.ntbar .rcl__btn{appearance:none;display:inline-flex;align-items:center;justify-content:center;gap:6px;height:38px;padding:0 14px;border-radius:6px;font:600 13px/1 Inter,-apple-system,system-ui,sans-serif;white-space:nowrap;cursor:pointer;box-sizing:border-box;}' +
    '.ntbar .ntbar__h,.ntbar .rcl__btn--ghost{background:#fff;border:1px solid #d1d5db;color:#111827;width:auto;}' +
    '.ntbar .ntbar__h svg{width:16px;height:16px;flex:0 0 auto;}' +
    '.ntbar .ntbar__h:hover:not(:disabled),.ntbar .rcl__btn--ghost:hover:not(:disabled){border-color:#9ca3af;color:#111827;}' +
    '.ntbar .ntbar__h:disabled,.ntbar .rcl__btn--ghost:disabled{opacity:.45;cursor:default;}' +
    '.ntbar .ntbar__cancel{background:#eef0f3;border:0;color:#111827;}' +
    '.ntbar .ntbar__cancel:hover{background:#e3e6ea;}' +
    '.ntbar .rcl__btn--primary{background:#019aff;background-image:none;color:#fff;border:0;min-width:76px;}' +
    '.ntbar .rcl__btn--primary:not(:disabled):hover{background:#0088e0;}' +
    '.ntbar .rcl__btn--primary:disabled{opacity:.5;}' +
    '@media (max-width:700px){.rcl__foot.ntbar{padding:12px 14px 16px;gap:6px;}' +
    '.ntbar>div{gap:6px !important;}' +
    /* 5 buttons on a 343 px sheet (iPhone SE): 14px labels, 12px sides, no min width */
    '.ntbar .ntbar__h,.ntbar .rcl__btn{height:42px;font-size:14px;padding:0 12px;}.ntbar .rcl__btn--primary{min-width:0;}' +
    '.ntbar .ntbar__h{width:42px;padding:0;}.ntbar .ntbar__h span{display:none;}' +
    '.ntbar .ntbar__h svg{width:18px;height:18px;}}';

  /* PDP toolbar button + wrapper (Justin 7-9): dashed pill matching Edit with
     AI (recipe mirrored from assets/nt-ai-toolbar.css — that file owns the
     shared toolbar look; .nt-recolor-btn/.nt-ai-actions are OURS, injected
     only on the three target templates so no shared-toolbar rules change).
     Desktop: stacked column so Recolor sits directly UNDER Edit with AI.
     Mobile (<=768px): row — Recolor to the RIGHT of Edit with AI, icon
     hidden, plus "Remove Background" -> "Remove BG" label swap. */
  var PDP_CSS = '' +
    /* Flat-context pair: Edit with AI + Recolor side by side (natural DOM
       order — Edit left, Recolor right). Desktop single-upload uses the
       3-col RAIL (column cards) instead, so this shapes: the dtf-transfers
       multi-upload modal tiles, the v2 builder blocks (large view), and
       mobile bars. v2 builder COMPACT view stacks the pair (below). */
    '.nt-ai-actions{display:flex;flex-direction:row;gap:6px;margin-left:auto;max-width:none;flex:0 0 auto;min-width:0;align-items:stretch;}' +
    '.nt-ai-actions .edit-with-ai:not(.nt-recolor-btn){margin-left:0 !important;}' +
    /* v1 multi-modal tiles are ~290px of toolbar: toggles + Edit+Recolor
       can't share one line with the long "Remove Background" label — swap to
       "Remove BG" at every width in tiles that carry our pair (Justin 7-9
       "Remove Background should say Remove BG then insert Recolor to the
       right of Edit with AI"). Wrap stays as a fallback for tighter tiles;
       with the short label everything fits one line and nothing wraps. */
    'multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-bgr-toggle-label--full{display:none !important;}' +
    'multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-bgr-toggle-label--short{display:inline !important;}' +
    /* v1 (dtf-transfers) + uv by-size (Justin 7-10 "uv multi upload must match
       dtf by size"): shorten "Edit with AI" -> "Edit w/ AI" in the multi
       tiles (Justin 7-9). The label is theme markup, so swap it the same way
       ai-toolbar-redesign does: hide the span, re-inject via ::after
       (inherits the button font; hover rules recolor it through `:hover *`).
       Every dtf-gated rule below carries a cloned uv-by-size-middle-scroll
       selector so the two templates' toolbars stay pixel-identical. */
    'body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex>span,' +
    'body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex>span{display:none !important;}' +
    'body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex::after,' +
    'body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex::after{content:"Edit w/ AI";}' +
    /* v1 tiles match the approved mobile single-upload bar at EVERY width
       (Justin 7-9 "v1 multi upload on desktop should match v1 mobile single
       upload"): 12px labels on all four controls + desktop icons on both
       pills. */
    'multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai .edit-with-aiFlex::after,' +
    'multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai .edit-with-aiFlex>span,' +
    'multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .nt-recolor-btn>span{font-size:12px !important;font-weight:600 !important;line-height:1.2 !important;}' +
    'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch,' +
    'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch,' +
    'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch *,' +
    'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch *,' +
    'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-bgr-toggle-label,' +
    'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-bgr-toggle-label{font-size:12px !important;}' +
    '@media (min-width:769px){' +
      /* the engine's bar is row-REVERSE + nowrap !important, so when the
         pair + icons outgrow the ~310px column the toggles overflowed LEFT
         out of the modal (Justin 7-9 screenshot). Force a plain row that
         wraps, order-pinned toggles-then-pair: one line when it fits
         (toggles left, pair right), pair drops to its own line when not. */
      'multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions){flex-direction:row !important;flex-wrap:wrap !important;row-gap:8px !important;column-gap:6px !important;justify-content:space-between !important;max-width:100% !important;box-sizing:border-box !important;}' +
      'multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) toggle-options{flex:0 0 auto !important;order:1 !important;margin:0 !important;}' +
      'multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions{order:2 !important;}' +
      /* desktop tiles: pills read big next to the toggles even at the same
         12px (Justin 7-9 image 2) — take the pill labels down a notch */
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai .edit-with-aiFlex::after,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai .edit-with-aiFlex::after,' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai .edit-with-aiFlex>span,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai .edit-with-aiFlex>span,' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .nt-recolor-btn>span,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .nt-recolor-btn>span{font-size:11px !important;}' +
      /* ...and NO icons on desktop tiles so the whole bar keeps one line
         (Justin 7-9); icons are mobile-only there. Pills padded 12->8px and
         toggle labels matched to the pills' 11px: the ~310px column needs
         the trim (177px toggles + 151px pills didn't fit). */
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .nt-ai-actions.nt-ai-actions .nt-recolor-btn svg,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .nt-ai-actions.nt-ai-actions .nt-recolor-btn svg,' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .nt-ai-actions.nt-ai-actions .edit-with-ai .edit-with-aiFlex img,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .nt-ai-actions.nt-ai-actions .edit-with-ai .edit-with-aiFlex img{display:none !important;}' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai{padding-left:5px !important;padding-right:5px !important;}' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions{gap:4px !important;}' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions),' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions){padding-left:8px !important;padding-right:6px !important;}' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch,' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch *,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch *,' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-bgr-toggle-label,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-bgr-toggle-label{font-size:11px !important;}' +
    '}' +
    /* v2 Power Builder blocks: the multi-upload APP's own stylesheet on this
       theme hides the Edit button's text span (live's app version shows it —
       computed display:block there, none here), leaving an icon-only dashed
       stub (Justin 7-9 images 1/2/5 "missing Edit with AI text"). Force the
       span back on; as a flex item inline/block render identically, so this
       is a no-op wherever the app already shows it. */
    'body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open uploaded-files-block .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex > span{display:inline !important;}' +
    /* ...and kill the redesign snippet's ::before sparkle there — with the
       img restored by power-builder.css it doubled up (Justin 7-9 "2 icons
       with edit with AI instead of 1"). */
    'body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open uploaded-files-block .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex::before{content:none !important;display:none !important;}' +
    /* v2 Power Builder blocks — compact view: Recolor UNDER Edit with AI;
       large view keeps the row (Justin 7-9, images 1/2). Selector weight
       mirrors power-builder.css's doubled-class pattern. */
    'body.pb-compact:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open uploaded-files-block .nt-ai-actions{flex-direction:column !important;align-items:stretch !important;}' +
    'body.pb-compact:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open uploaded-files-block .nt-ai-actions .nt-recolor-btn{width:100% !important;box-sizing:border-box !important;}' +
    /* BOTH builder views (compact + larger): Edit/Recolor go UNDER Remove
       BG + Upscale, left-aligned with them (Justin 7-9 twice). The pair's
       DOM slot is above the toggles, so re-order the toolbar column:
       everything defaults to 0, our pair sinks last. margin-left:0
       overrides the base wrapper's margin-left:auto. Compact alone stacks
       the pair vertically (rule above); larger view keeps the row. */
    /* the .pb-builder-open chain is stacked ×6 to out-rank power-builder
       css's compact rule (7 classes: gap:5px !important + margin-top:2px
       on the button) so every gap really lands at the toggles' 7px */
    'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .multiupload-rebuild-tools-inner:has(.nt-ai-actions){display:flex !important;flex-direction:column !important;gap:7px !important;}' +
    'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .multiupload-rebuild-tools-inner .nt-ai-actions{order:9 !important;margin-left:0 !important;margin-right:auto !important;align-self:flex-start !important;gap:7px !important;}' +
    /* uniform rhythm (Justin 7-9): every AI-tool gap = the 7px between the
       two toggles — strip the buttons' stray margins that inflated it */
    'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .edit-with-ai,' +
    'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .nt-recolor-btn{margin:0 !important;}' +
    /* keep the palette PURPLE in the builder at rest AND on hover — the
       builder's hover rule paints `button.edit-with-ai:hover *` #111827
       !important, which blacked out the icon (Justin 7-9 image 1). Shadow
       and lift stay; only the icon color is pinned. */
    /* the builder rule matches `:hover *` — i.e. the svg's CHILD circles and
       paths too, whose fill/stroke resolve currentColor at their OWN level,
       so pinning only the <svg> still rendered black (Justin 7-9 round 2).
       Pin the descendants as well, both states. */
    'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .nt-recolor-btn svg,' +
    'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .nt-recolor-btn svg *,' +
    'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .nt-recolor-btn:hover svg,' +
    'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .nt-recolor-btn:hover svg *{color:#7C3AED !important;}' +
    /* Flat-bar Recolor carries the edit-with-ai CLASS, so the shared toolbar
       stylesheets give it the IDENTICAL faint-dash pill + hover as Edit with
       AI on every breakpoint (Justin 7-9) — we add only layout + the icon. */
    '.nt-recolor-btn{display:inline-flex;align-items:center;justify-content:center;gap:7px;cursor:pointer;font-family:inherit;white-space:nowrap;}' +
    '.nt-recolor-btn svg{width:15px;height:15px;flex:0 0 auto;color:#7C3AED;}' +
    /* Desktop 3-col "AI TOOLS" rail (nt-dtf-3col.js): Recolor rides as its own
       card directly UNDER the Edit with AI card, matching that pill's COMPUTED
       look exactly (radius 12, 1.5px dashed #CDCED1, 12px/600 #5A5B60 text,
       translucent white fill, 45px tall). Selectors carry body.image-uploaded
       to out-rank nt-dtf-3col.css's `body.image-uploaded .nt-ai-card`
       card-box rules — without it the Recolor card kept its own white box and
       the button didn't fill it (Justin 7-9 "buttons have to match"). */
    'html body.image-uploaded .nt-ai-card.nt-ai-card--recolor{padding:0 !important;border:0 !important;background:transparent !important;box-shadow:none !important;}' +
    'html body.image-uploaded .nt-ai-card--recolor .nt-recolor-btn{width:100% !important;max-width:100% !important;box-sizing:border-box !important;transform:none !important;}' +
    /* rail hover is JS-driven (white fill only, like Edit with AI) — the
       palette icon KEEPS its purple at all times. The :hover svg whitening
       below applies only to the flat/mobile bar's blue-fill hover. */
    'html body.image-uploaded .nt-ai-card--recolor .nt-recolor-btn svg{color:#7C3AED !important;}' +
    '@media (max-width:768px){' +
      /* Mobile bar (Justin 7-9): Remove BG toggle, Upscale toggle, Recolor,
         Edit with AI — left to right, ALL FOUR the same distance apart. The
         toolbar's flex tree varies (toggles inside <toggle-options> OR as
         .style3 wrappers in .fileupload_bg_options), so FLATTEN the wrappers
         with display:contents: every control becomes a direct flex item of
         the bar and one space-between distributes them evenly. Scoped to
         bars containing our injected pair. */
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) toggle-options,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .fileupload_bg_options,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions{display:contents !important;}' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions){display:flex !important;flex-direction:row !important;flex-wrap:nowrap !important;justify-content:space-between !important;align-items:center !important;gap:0 !important;}' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch.bgRemover,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .style3:has(.bgRemover){order:1 !important;margin:0 !important;}' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .switch.superRes,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .style3:has(.superRes){order:2 !important;margin:0 !important;}' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-recolor-btn{order:3 !important;}' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .nt-ai-actions .edit-with-ai:not(.nt-recolor-btn){order:4 !important;margin:0 !important;}' +
      '.nt-ai-actions .edit-with-ai:not(.nt-recolor-btn){width:auto !important;flex:0 1 auto !important;}' +
      '.nt-recolor-btn{width:auto;flex:0 1 auto;}' +
      /* icons stay ON at mobile too (Justin 7-9: "add the icons back") */
      /* v1 mobile (single-upload flat bar included): "Edit w/ AI" (7-9) */
      'html body.template-product-by-size-middle-scroll .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex>span,' +
      'html body.template-product-uv-by-size-middle-scroll .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex>span{display:none !important;}' +
      'html body.template-product-by-size-middle-scroll .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex::after,' +
      'html body.template-product-uv-by-size-middle-scroll .multiupload-rebuild-tools-inner:has(.nt-ai-actions) .edit-with-ai:not(.nt-recolor-btn) .edit-with-aiFlex::after{content:"Edit w/ AI";}' +
      /* v1 tiles, mobile: BOTH icons on — the sparkle img (hidden by the
         engine markup styles) and the palette svg, which nt-ai-toolbar's
         phone-fit block kills via `html body .edit-with-ai svg{display:none
         !important}` and our Recolor wears that class; tools-inner tripled
         to out-rank it (Justin 7-9 "no purple icon on mobile"). */
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner .nt-ai-actions .nt-recolor-btn svg,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner .nt-ai-actions .nt-recolor-btn svg{display:inline-block !important;width:13px !important;height:13px !important;}' +
      'html body.template-product-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner .nt-ai-actions .edit-with-ai .edit-with-aiFlex img,' +
      'html body.template-product-uv-by-size-middle-scroll multi-upload-wrapper .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner .nt-ai-actions .edit-with-ai .edit-with-aiFlex img{display:inline-block !important;width:13px !important;height:13px !important;margin:0 4px 0 0 !important;flex:0 0 auto !important;position:static !important;}' +
      /* single-upload mobile bar only (NOT multi tiles, NOT the pb builder):
         one uniform label size on all four controls — first bumped to 13
         ("too small"), then settled at 12 with icons back on (Justin 7-9).
         The tools-inner class is TRIPLED to out-rank the phone-fit rules in
         nt-ai-toolbar.css (11px !important) that were still winning on
         Upscale and Edit w/ AI (Justin 7-9 round 2); `.switch *` catches
         the Upscale label however deep it nests. */
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .switch,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .switch *,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .nt-bgr-toggle-label,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .nt-recolor-btn>span,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .edit-with-ai .edit-with-aiFlex,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .edit-with-ai .edit-with-aiFlex>span,' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .edit-with-ai .edit-with-aiFlex::after{font-size:12px !important;}' +
      /* desktop's icons, back on (Justin 7-9): the redesign snippet hides the
         sparkle img on non-multi bars — re-show it; palette svg sized to the
         12px text */
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .edit-with-ai .edit-with-aiFlex img{display:inline-block !important;width:13px !important;height:13px !important;margin:0 4px 0 0 !important;position:static !important;flex:0 0 auto !important;}' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .nt-recolor-btn svg{display:inline-block !important;width:13px !important;height:13px !important;}' +
      'html body:where(:not(.pb-builder-open)) .multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner.multiupload-rebuild-tools-inner:has(.nt-ai-actions):where(:not(multi-upload-wrapper *)) .switch .slider{transform:scale(1.1) !important;transform-origin:left center !important;}' +
      /* "Remove Background" -> "Remove BG" so the row fits (spans rendered
         by __multiuploadSnippets on these templates) */
      '.nt-bgr-toggle-label--full{display:none !important;}' +
      '.nt-bgr-toggle-label--short{display:inline !important;}' +
    '}' +
    /* v2 builder on mobile (pb's own breakpoint is 800, not 768): Edit with
       AI + Recolor side by side, left-aligned with Upscale, icons INSIDE
       the pills (Justin 7-9 — the sparkle was rendering outside the dashed
       border; inline-flex + static img contains it; Recolor svg re-shown
       over the generic mobile hide). Chain stacked like the other pb rules. */
    '@media (max-width:800px){' +
      'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .multiupload-rebuild-tools-inner .nt-ai-actions{flex-direction:row !important;align-items:stretch !important;gap:7px !important;margin-left:0 !important;margin-right:auto !important;}' +
      'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .nt-recolor-btn{width:auto !important;box-sizing:border-box !important;}' +
      'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .edit-with-ai{align-self:auto !important;margin:0 !important;display:inline-flex !important;align-items:center !important;}' +
      'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .nt-recolor-btn svg{display:inline-block !important;width:13px !important;height:13px !important;}' +
      'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .edit-with-ai .edit-with-aiFlex{display:inline-flex !important;align-items:center !important;gap:5px !important;}' +
      'html body:is(.template-product-power-builder,.template-product-uv-by-size-middle-scroll).pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open.pb-builder-open uploaded-files-block .nt-ai-actions .edit-with-ai .edit-with-aiFlex img{display:inline-block !important;width:13px !important;height:13px !important;margin:0 !important;position:static !important;flex:0 0 auto !important;}' +
    '}';

  function injectCss() {
    if (!document.getElementById('rcl-css')) {
      var st = document.createElement('style');
      st.id = 'rcl-css';
      st.textContent = CSS;
      document.head.appendChild(st);
    }
  }
  function injectPdpCss() {
    if (document.getElementById('rcl-pdp-css')) return;
    var st = document.createElement('style');
    st.id = 'rcl-pdp-css';
    st.textContent = PDP_CSS;
    document.head.appendChild(st);
  }

  /* dialog state */
  var state = null;

  /* adapter: { baseSrc, seedReplacements, canReset, busyApplyMsg?,
                apply(hostedUrl, replacements, baseSrc) -> Promise,
                reset() -> Promise } */
  /* Step 2 grid: Transparent (3 cells) + these 20 in this fixed order + Custom = 2 rows × 12 (handoff v2) */
  var STD_COLORS = [['#FFFFFF', 'White'], ['#D4D5D8', 'Light Grey'], ['#8A8B90', 'Grey'], ['#48494B', 'Charcoal'],
    ['#1A1A1A', 'Black'], ['#F5E6C8', 'Cream'], ['#C8A27A', 'Tan'], ['#7A4A2A', 'Brown'], ['#8B1E3F', 'Maroon'],
    ['#FF394C', 'Red'], ['#FF8FB1', 'Pink'], ['#F27708', 'Orange'], ['#F9DE43', 'Yellow'], ['#21AF70', 'Green'],
    ['#14B8A6', 'Teal'], ['#7CCBFF', 'Sky Blue'], ['#019AFF', 'Ninja Blue'], ['#003DA6', 'Dark Blue'],
    ['#0D1B4F', 'Navy'], ['#7B3FE4', 'Purple']];
  var STD_RGB = STD_COLORS.map(function (c) { return hexToRgb(c[0]); });
  function isStd(rgb) { return STD_RGB.some(function (s) { return rgbDist2(s, rgb) < 12 * 12; }); }
  var POINT_SVG = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 11V5.5a1.5 1.5 0 0 1 3 0V11"/><path d="M12 10.5V9a1.5 1.5 0 0 1 3 0v2"/><path d="M15 10.5a1.5 1.5 0 0 1 3 0V15a6 6 0 0 1-6 6h-1a6 6 0 0 1-4.6-2.2L4 15.5a1.5 1.5 0 0 1 2.3-1.9L9 16.5"/></svg>';

  /* By Size v5: a framed design opens these dialogs on its frame (adapter.viewRect = {x,y,w,h}, fractions of the
     base). Display only — the edit and Apply still cover the whole picture, so the frame can move later. */
  function viewToggle(onChange) {
    var el = document.createElement('div');
    el.className = 'rcl__view'; el.setAttribute('role', 'group'); el.setAttribute('aria-label', 'Preview area');
    el.innerHTML = '<button type="button" class="is-on" data-v="crop" aria-pressed="true">Cropped</button><button type="button" data-v="full" aria-pressed="false">Full image</button>';
    el.addEventListener('click', function (e) {
      var b = e.target.closest('button'); if (!b) return;
      [].forEach.call(el.children, function (n) { n.classList.toggle('is-on', n === b); n.setAttribute('aria-pressed', n === b ? 'true' : 'false'); });
      onChange(b.getAttribute('data-v') === 'crop');
    });
    return el;
  }

  function openRecolor(adapter) {
    injectCss();
    var baseSrc = adapter.baseSrc;
    /* opt-in (By Size v5): the picture may be prepared just before opening (e.g. the erase layer applied) — a Promise of
       the URL; it falls back to adapter.baseFallback. Every other caller passes a string, unchanged */
    if (baseSrc && typeof baseSrc.then === 'function') {
      baseSrc.then(function (u) { openRecolor(Object.assign({}, adapter, { baseSrc: u || adapter.baseFallback || '' })); },
        function () { openRecolor(Object.assign({}, adapter, { baseSrc: adapter.baseFallback || '' })); });
      return;
    }
    if (!baseSrc) { toast('This image cannot be recolored.'); return; }

    var ov = document.createElement('div'), V5BAR = !!window.__bsv5Tools;
    ov.className = 'rcl-ov';
    ov.innerHTML = '' +
      '<div class="rcl" role="dialog" aria-label="Swap Colors">' +
        '<div class="rcl__head rcl__head--v2' + (V5BAR ? ' rcl__head--v5' : '') + '"><div class="rcl__headtx"><h3 class="rcl__title">Swap Colors</h3>' +
          '<p class="rcl__desc">Swap colors everywhere, change one part, fill empty areas, or make things transparent.</p></div>' +
          '<button type="button" class="rcl__close" aria-label="Close"><svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg></button></div>' +
        '<div class="rcl__body">' +
          '<div class="rcl__left">' +
            '<div class="rcl__stage"><div class="rcl__frame"><canvas class="rcl__img"></canvas><canvas class="rcl__ovl"></canvas></div>' +
              '<div class="rcl__taphint">' + POINT_SVG + '<span>Select any part of the image to change its color or fill it in</span></div>' +
              '<div class="rcl__chip" hidden><span class="rcl__chipsw"></span><span class="rcl__chiptx"></span></div>' +
              '<div class="rcl__busy"><span class="rcl__spin"></span><span style="font:600 13px/1.4 \'DM Sans\',sans-serif;color:#374151">Reading colors…</span></div></div>' +
            '<button type="button" class="rcl__compare">Hold to see the original</button>' +
          '</div>' +
          (V5BAR ?
          /* By Size v5 (Owen 11-04): step 2 reads heading -> hint -> colors -> legend; 9 bigger swatches a row */
          '<div class="rcl__right rcl__right--v5">' +
            '<div class="rcl__s1"><div class="rcl__step"><span class="rcl__num">1</span><h2>Select the color or part of your image you want to change</h2></div><div class="rcl__sw"></div></div>' +
            '<div class="rcl__step2"><div class="rcl__step"><span class="rcl__num">2</span><h2>Pick a new color</h2></div>' +
              '<p class="rcl__hint2 rcl__palhint"></p>' +
              '<div class="rcl__grid" role="group" aria-label="More colors"></div>' +
              '<div class="rcl__legendrow"><span class="rcl__legend"><span class="rcl__dot"></span>Already in your image</span></div></div>'
          :
          '<div class="rcl__right">' +
            '<div class="rcl__s1"><div class="rcl__step"><span class="rcl__num">1</span><h2>Swap a color below or Select a part of your image</h2></div><div class="rcl__sw"></div></div>' +
            '<div class="rcl__step2"><div class="rcl__step"><span class="rcl__num">2</span><h2>Pick a new color</h2></div>' +
              '<div class="rcl__legendrow"><span class="rcl__legend"><span class="rcl__dot"></span>Already in your image</span></div>' +
              '<div class="rcl__grid" role="group" aria-label="More colors"></div><p class="rcl__hint2 rcl__palhint"></p></div>') +
            '<div class="rcl__tolblk"><p class="rcl__tollab">Tolerance threshold</p>' +
              '<input type="range" class="rcl__tolin" min="0" max="100" value="35" step="1" aria-label="Tolerance threshold">' +
              '<div class="rcl__tolends"><span>Fewer</span><span>More</span></div></div>' +
          '</div>' +
        '</div>' +
        (V5BAR ?
        /* By Size v5: the shared tool bar (.ntbar, same as the Eraser's) — same buttons, same handlers */
        '<div class="rcl__foot ntbar">' +
          '<div style="display:flex">' +
            '<button type="button" class="ntbar__h rcl__undo" aria-label="Undo" title="Undo" disabled>' + NTBAR_UNDO + '<span>Undo</span></button>' +
            '<button type="button" class="ntbar__h rcl__redo" aria-label="Redo" title="Redo" disabled><span>Redo</span>' + NTBAR_REDO + '</button>' +
            '<button type="button" class="rcl__btn rcl__btn--ghost rcl__reset" hidden>Reset</button>' +   /* shown by updateApply when it would do something */
          '</div>' +
          '<div style="display:flex">' +
            '<button type="button" class="rcl__btn ntbar__cancel rcl__cancel">Cancel</button>' +
            '<button type="button" class="rcl__btn rcl__btn--primary rcl__apply" disabled>Apply</button>' +
          '</div>' +
        '</div>' :
        '<div class="rcl__foot">' +
          '<div style="display:flex;gap:8px;align-items:center">' +
            '<button type="button" class="rcl__hbtn rcl__undo" aria-label="Undo" title="Undo" disabled><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7v6h6"/><path d="M21 17a9 9 0 0 0-15-6.7L3 13"/></svg></button>' +
            '<button type="button" class="rcl__hbtn rcl__redo" aria-label="Redo" title="Redo" disabled><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 7v6h-6"/><path d="M3 17a9 9 0 0 1 15-6.7L21 13"/></svg></button>' +
            '<button type="button" class="rcl__btn rcl__btn--ghost rcl__reset"><span class="rcl__lbl--full">Reset image</span><span class="rcl__lbl--short">Reset</span></button>' +
          '</div>' +
          '<div style="display:flex;gap:10px">' +
            '<button type="button" class="rcl__btn rcl__btn--ghost rcl__cancel">Cancel</button>' +
            '<button type="button" class="rcl__btn rcl__btn--primary rcl__apply" disabled>Apply</button>' +
          '</div>' +
        '</div>') +
      '</div>';
    document.body.appendChild(ov);

    state = {
      ov: ov, adapter: adapter, baseSrc: baseSrc,
      palette: [],                /* step 1 swatches [{rgb, hex}] from the CURRENT image */
      replacements: {},           /* older saved color swaps (seeded only; new edits are shape ops) */
      shapes: [],                 /* edits in order, see the engine comment */
      sel: null,                  /* {scope,fx,fy,rgb,transparent,base,mask,outline,op,via,tol,id} */
      hover: null, customHex: '', added: [], stable: [], origHex: [], opMasks: [],
      working: null, workingBase: null, current: null,
      previewCanvas: ov.querySelector('.rcl__img'), ovl: ov.querySelector('.rcl__ovl'),
      frame: ov.querySelector('.rcl__frame'), chip: ov.querySelector('.rcl__chip'),
      busyEl: ov.querySelector('.rcl__busy'),
      hist: [], histIdx: -1, histSession: null, sessionSeq: 0, ants: 0, antsTimer: null, comparing: false
    };
    (adapter.seedReplacements || []).forEach(function (r) {
      if (!r) return;
      if (r.type === 'shape') { state.shapes.push(JSON.parse(JSON.stringify(r))); return; }
      state.replacements[r.targetColor.toUpperCase()] = { mode: r.mode, newColor: r.newColor, flatFill: r.flatFill !== false, boundaryBias: r.boundaryBias || 0 };
    });
    pushHist(); /* baseline snapshot = the state the dialog opened with */
    state.view = adapter.viewRect || null; state.viewOn = !!state.view; state.viewOnly = !!adapter.viewOnly;
    /* adapter.viewOnly (By Size v5, Owen 11-02, opt-in): a cropped design shows only its crop — no Cropped / Full image
       toggle. Without it: today's toggle. */
    /* adapter.notice (By Size v5, opt-in): one line above the picture (e.g. "Swap Colors will merge your filter…") */
    if (adapter.notice) {
      var nt = document.createElement('p');
      nt.className = 'rcl__notice';
      nt.style.cssText = 'margin:0 0 10px;padding:8px 12px;border-radius:8px;background:#fff7e6;border:1px solid #ffd591;color:#874d00;font-size:13px;line-height:1.4';
      nt.textContent = adapter.notice;
      var lft = ov.querySelector('.rcl__left'); if (lft) lft.insertBefore(nt, lft.firstChild);
    }
    if (state.view && !adapter.viewOnly) {
      var left = ov.querySelector('.rcl__left');
      left.insertBefore(viewToggle(function (on) { if (!state) return; state.viewOn = on; fitFrame(); drawOverlay(); }), left.firstChild);
    }

    ov.querySelector('.rcl__close').addEventListener('click', closeDialog);
    ov.querySelector('.rcl__cancel').addEventListener('click', closeDialog);
    ov.addEventListener('click', function (e) { if (e.target === ov) closeDialog(); });
    ov.querySelector('.rcl__apply').addEventListener('click', applyAll);
    ov.querySelector('.rcl__reset').addEventListener('click', resetImage);
    ov.querySelector('.rcl__undo').addEventListener('click', function () { stepHist(-1); });
    ov.querySelector('.rcl__redo').addEventListener('click', function () { stepHist(1); });

    var cmp = ov.querySelector('.rcl__compare');
    function showOrig(on) {
      if (!state || !state.workingBase) return;
      state.comparing = on;
      state.previewCanvas.getContext('2d').putImageData(on ? state.workingBase : state.current, 0, 0);
      syncHint(); drawOverlay();
    }
    ['mousedown', 'touchstart'].forEach(function (ev) { cmp.addEventListener(ev, function (e) { e.preventDefault(); showOrig(true); }); });
    ['mouseup', 'mouseleave', 'touchend', 'touchcancel'].forEach(function (ev) { cmp.addEventListener(ev, function () { showOrig(false); }); });

    var tol = ov.querySelector('.rcl__tolin');
    /* By Size v5 (snappy tools 11-03): a drag fired a full re-render per input event (~80 ms each, queued up on slower
       computers). Now one light render per frame with the newest value, and the swatches refresh on release. Other
       pages keep the per-input render. */
    var tolRaf = 0, tolDrag = false;
    function onTol(light) {
      var s = state && state.sel;
      if (!s) return;
      s.tol = Number(tol.value) || 0;
      remask();
      if (s.op) { s.op.tol = s.tol; renderPreview(light); pushHist('sel:' + s.id); } else drawOverlay();
    }
    tol.addEventListener('input', function () {
      if (!window.__bsv5Tools) return onTol(false);
      tolDrag = true;
      if (!tolRaf) tolRaf = requestAnimationFrame(function () { tolRaf = 0; onTol(true); });
    });
    tol.addEventListener('change', function () {
      if (!window.__bsv5Tools || !tolDrag) return;
      tolDrag = false;
      if (tolRaf) { cancelAnimationFrame(tolRaf); tolRaf = 0; onTol(true); }
      if (state && state.sel && state.sel.op && state.current) { state.palette = paletteOf(visibleOf(state.current), state.added); buildSwatchRow(); syncControls(); }
    });

    /* pointer events live on the image area only (not the frame padding) */
    var frame = state.frame, raf = 0, lastEv = null;
    function pixelAt(e) {
      var c = state.previewCanvas, r = c.getBoundingClientRect();
      if (!r.width || !r.height) return null;
      var x = Math.floor((e.clientX - r.left) / r.width * c.width), y = Math.floor((e.clientY - r.top) / r.height * c.height);
      if (x < 0 || y < 0 || x >= c.width || y >= c.height) return null;
      return { x: x, y: y, i: y * c.width + x };
    }
    frame.addEventListener('mousemove', function (e) {
      lastEv = e;
      if (raf) return;
      raf = requestAnimationFrame(function () { raf = 0; if (state && lastEv) hoverAt(pixelAt(lastEv)); });
    });
    frame.addEventListener('mouseleave', function () { if (!state) return; state.hover = null; syncHint(); drawOverlay(); });
    frame.addEventListener('click', function (e) {
      if (!state || !state.current) return;
      var pt = pixelAt(e);
      if (pt) selectAt(pt);
    });
    /* clicking off the selection — blank dialog space, the stage around the picture, the copy —
       drops it and its marching ants (Justin 10-30). The picture, swatches, color picker, tolerance
       and every button keep their own behaviour. */
    ov.querySelector('.rcl').addEventListener('click', function (e) {
      if (!state || !state.sel) return;
      if (e.target.closest('button, input, label, select, a, .rcl__frame, .rcl__step2, .rcl__tolblk')) return;
      clearSelection();
      syncControls(); syncHint(); drawOverlay();
    });

    state.onResize = function () { fitFrame(); };
    window.addEventListener('resize', state.onResize);

    Promise.all([loadImageAny(corsCandidates(baseSrc)), loadR2()]).then(function (all) {
      var res = all[0];
      state.resolvedSrc = res.url; /* CORS-readable twin of baseSrc — used for pixel work */
      var cv = drawScaled(res.img, MAX_WORKING);
      var ctx = cv.getContext('2d');
      state.working = ctx.getImageData(0, 0, cv.width, cv.height);
      state.workingBase = ctx.getImageData(0, 0, cv.width, cv.height);
      state.previewCanvas.width = state.ovl.width = cv.width;
      state.previewCanvas.height = state.ovl.height = cv.height;
      state.busyEl.style.display = 'none';
      /* the uploaded image's own colors, computed ONCE: they open the step 2 grid for good */
      R2_AN = null; r2LutCache.clear(); r2AnCache.clear(); r2Seen = [];
      if (R2) { try { r2An(state.workingBase.data, state.workingBase.width, state.workingBase.height); } catch (e) { console.warn('[studio-recolor] engine analysis failed, using v1', e); R2 = null; } }
      state.origHex = paletteOf(state.workingBase, []).map(function (p) { return p.hex; });
      fitFrame();
      renderPreview(); updateApply();
    }).catch(function (err) {
      console.error('[studio-recolor] load failed', err);
      state.busyEl.querySelector('span:last-child').textContent = 'Could not read this image.';
      state.busyEl.querySelector('.rcl__spin').style.display = 'none';
    });
    syncControls();
  }

  /* the image area keeps the image's aspect ratio, as large as the frame allows */
  function fitFrame() {
    if (!state || !state.previewCanvas.width) return;
    var stage = state.frame.parentNode, cs = getComputedStyle(stage);
    var cw = state.previewCanvas.width, ch = state.previewCanvas.height;
    var maxW = stage.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    var maxH = window.innerHeight * (window.matchMedia('(max-width:700px)').matches ? 0.3 : 0.42);
    var v = state.viewOn && state.view, ar = v ? (cw * v.w) / (ch * v.h) : cw / ch;
    var fw = Math.round(Math.max(40, Math.min(maxW, maxH * ar)));
    state.frame.style.width = fw + 'px';
    /* cropped view: the frame shows only the crop area; the canvases scale up behind it (pixelAt reads their rects) */
    state.frame.style.height = v ? Math.round(fw / ar) + 'px' : '';
    state.frame.style.overflow = v ? 'hidden' : '';
    [state.previewCanvas, state.ovl].forEach(function (c) {
      c.style.position = v ? 'absolute' : '';
      c.style.left = v ? (-v.x / v.w * 100) + '%' : '';
      c.style.top = v ? (-v.y / v.h * 100) + '%' : '';
      c.style.width = v ? (100 / v.w) + '%' : '';
      c.style.height = v ? (100 / v.h) + '%' : '';
    });
  }

  /* ── selecting: tap the image = one part; tap a swatch = that color everywhere ── */
  function newSel(props) {
    var cur = state.current;
    var s = Object.assign({ base: new ImageData(new Uint8ClampedArray(cur.data), cur.width, cur.height), op: null, via: null,
      tol: state.sel ? state.sel.tol : SHAPE_TOL, id: ++state.sessionSeq }, props);
    state.sel = s;
    remask();
    state.hover = null;
    startAnts();
    syncControls(); drawOverlay();
  }
  function selectAt(pt) {
    var cur = state.current, W = cur.width, H = cur.height, d = cur.data, p = pt.i * 4;
    var transparent = d[p + 3] < 128, via = null;
    if (transparent) {
      /* inside a part erased earlier? re-use that part instead of flooding the whole background */
      for (var k = state.shapes.length - 1; k >= 0; k--) {
        var o = state.shapes[k], m = state.opMasks[k];
        if (o.target === 'transparent' && !o.seedT && m && m[pt.i]) { via = k; break; }
      }
    }
    newSel({ scope: 'part', fx: (pt.x + 0.5) / W, fy: (pt.y + 0.5) / H, rgb: [d[p], d[p + 1], d[p + 2]], transparent: transparent, via: via });
  }
  function selectSwatch(sw) {
    newSel({ scope: 'all', rgb: sw.rgb.slice(), color: sw.hex, transparent: false });
  }
  function remask() {
    var s = state.sel, b = s.base, W = b.width, H = b.height;
    if (s.scope === 'all') s.mask = globalSel(b.data, W, H, s.rgb, s.tol).mask;
    else if (s.via != null && state.opMasks[s.via]) s.mask = erasedMask(b.data, state.opMasks[s.via]);
    else s.mask = floodSel(b.data, W, H, Math.floor(s.fx * W), Math.floor(s.fy * H), s.tol).mask;
    s.outline = maskOutline(s.mask, W, H);
  }

  function hoverAt(pt) {
    if (!state.current || state.comparing) return;
    var chip = state.chip, s = state.sel;
    if (!pt || (s && s.mask[pt.i])) { if (state.hover) { state.hover = null; syncHint(); drawOverlay(); } return; }
    if (state.hover && state.hover.mask[pt.i]) return;
    /* hover = a quick preview of the part under the pointer: the light flood keeps it instant on photos
       (the engine's region pass took 50-190ms per new area); the click selects with the engine */
    var cur = state.current, f = floodMask(cur.data, cur.width, cur.height, pt.x, pt.y, s ? s.tol : SHAPE_TOL), m = f.mask;
    if (f.transparent) {
      for (var k = state.shapes.length - 1; k >= 0; k--) {
        var o = state.shapes[k], em = state.opMasks[k];
        if (o.target === 'transparent' && !o.seedT && em && em[pt.i]) { m = em; break; }
      }
    }
    state.hover = { mask: m };
    var sw = chip.querySelector('.rcl__chipsw');
    sw.className = 'rcl__chipsw' + (f.transparent ? ' rcl__isck' : '');
    sw.style.background = f.transparent ? '' : rgbToHex(f.rgb[0], f.rgb[1], f.rgb[2]);
    chip.querySelector('.rcl__chiptx').textContent = f.transparent ? 'Empty spot · tap to fill it' : 'Tap to pick this part';
    syncHint(); drawOverlay();
  }
  /* tap hint shows only with no selection, no hover and compare not held; the chip only while hovering */
  function syncHint() {
    state.chip.hidden = !state.hover || state.comparing;
    state.ov.querySelector('.rcl__taphint').hidden = !!(state.sel || state.hover || state.comparing);
  }

  function drawOverlay() {
    if (!state || !state.ovl.width) return;
    var c = state.ovl, ctx = c.getContext('2d'), W = c.width, H = c.height;
    ctx.clearRect(0, 0, W, H);
    if (state.comparing) { state.ovc = null; return; }
    var img = ctx.createImageData(W, H), d = img.data, i, p, n = W * H, s = state.sel;
    if (state.hover) { var hm = state.hover.mask; for (i = 0; i < n; i++) if (hm[i] && !(s && s.mask[i])) { p = i * 4; d[p] = 1; d[p + 1] = 154; d[p + 2] = 255; d[p + 3] = 70; } }
    if (s) {
      /* no veil on the selection (Justin 10-29): just marching ants, black and white so they read on any color.
         Shrunk twice (Justin 10-30: -30%, then half of that): line w = 35% of the first cut's 2·round(1.5·k2)+1 px,
         dashes ~2.8 CSS px long, whatever the preview's scale */
      var k2 = W / Math.max(1, c.getBoundingClientRect().width || W);
      var w = Math.max(1, Math.round(0.35 * (2 * Math.round(1.5 * k2) + 1))), r0 = (w - 1) >> 1, r1 = w - 1 - r0;
      var dash = Math.max(2, Math.round(2.8 * k2));
      var rec = window.__bsv5Tools ? { ps: [], sums: [], x0: W, y0: H, x1: 0, y1: 0 } : null;
      for (var k = 0; k < s.outline.length; k++) {
        var o = s.outline[k], ox = o % W, oy = (o / W) | 0;
        var on = Math.floor((ox + oy + state.ants * dash / 4) / dash) & 1, v = on ? 255 : 17;
        for (var yy = Math.max(0, oy - r0); yy <= Math.min(H - 1, oy + r1); yy++)
          for (var xx = Math.max(0, ox - r0); xx <= Math.min(W - 1, ox + r1); xx++) {
            p = (yy * W + xx) * 4;
            d[p] = d[p + 1] = d[p + 2] = v; d[p + 3] = 255;
            if (rec) { rec.ps.push(p); rec.sums.push(ox + oy); if (xx < rec.x0) rec.x0 = xx; if (yy < rec.y0) rec.y0 = yy; if (xx > rec.x1) rec.x1 = xx; if (yy > rec.y1) rec.y1 = yy; }
          }
      }
    }
    ctx.putImageData(img, 0, 0);
    /* By Size v5 (snappy tools 11-03): keep what was drawn so the ants tick only re-shades the outline pixels */
    state.ovc = rec && rec.ps.length ? { img: img, ctx: ctx, sel: s, outline: s.outline, hover: state.hover, W: W, H: H,
      cw: c.getBoundingClientRect().width, dash: dash, ps: rec.ps, sums: rec.sums,
      x: rec.x0, y: rec.y0, w: rec.x1 - rec.x0 + 1, h: rec.y1 - rec.y0 + 1 } : null;
  }
  /* one ants step: the same pixels drawOverlay() would draw (same writes, same order), without rebuilding the overlay */
  function antsTick() {
    var c = state.ovc, s = state.sel;
    if (!c || state.comparing || c.sel !== s || !s || c.outline !== s.outline || c.hover !== state.hover ||
        c.W !== state.ovl.width || c.H !== state.ovl.height || c.cw !== state.ovl.getBoundingClientRect().width) return drawOverlay();
    var d = c.img.data, sh = state.ants * c.dash / 4;
    for (var i = 0, n = c.ps.length; i < n; i++) {
      var v = Math.floor((c.sums[i] + sh) / c.dash) & 1 ? 255 : 17, p = c.ps[i];
      d[p] = d[p + 1] = d[p + 2] = v;
    }
    c.ctx.putImageData(c.img, 0, 0, c.x, c.y, c.w, c.h);
  }
  function startAnts() {
    stopAnts();
    state.antsTimer = setInterval(function () { if (!state) return; state.ants = (state.ants + 1) % 8; antsTick(); }, 90);
  }
  function stopAnts() { if (state && state.antsTimer) { clearInterval(state.antsTimer); state.antsTimer = null; } }
  function clearSelection() { state.sel = null; state.histSession = null; stopAnts(); }

  /* Step 1: the picture's colors (no percentages), re-read after every change */
  function buildSwatchRow() {
    var row = state.ov.querySelector('.rcl__sw');
    row.innerHTML = '';
    state.palette.forEach(function (sw) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'rcl__sw1'; b.style.background = sw.hex; b.title = sw.hex; b.dataset.hex = sw.hex;
      b.addEventListener('click', function () { selectSwatch(sw); });
      row.appendChild(b);
    });
    buildGrid();
  }

  /* Step 2 grid (12 columns, grows by rows): Transparent (3 wide) · the uploaded image's colors (computed once)
     · the 20 standard colors (one within 12 of an original color is left out — the original takes its place)
     · colors made with the picker / later picture colors, append-only in order of first use · Custom.
     Swatches only ever get added. A dark dot marks a color that is in the image right now (originals excepted). */
  function nearStd(hex) {
    var c = hexToRgb(hex);
    for (var i = 0; i < STD_COLORS.length; i++) if (STD_COLORS[i][0] === hex || rgbDist2(STD_RGB[i], c) < 12 * 12) return STD_COLORS[i];
    return null;
  }
  function buildGrid() {
    var grid = state.ov.querySelector('.rcl__grid'), orig = state.origHex || [];
    state.palette.forEach(function (p) {
      if (state.stable.indexOf(p.hex) > -1) return;
      /* a near-copy of a color already listed is not a new color */
      if (state.stable.concat(state.origHex || []).some(function (h) { return rgbDist2(hexToRgb(h), p.rgb) < 18 * 18; })) return;
      state.stable.push(p.hex);
    });
    var covered = {};
    orig.forEach(function (h) { var n = nearStd(h); if (n) covered[n[0]] = 1; });
    var extra = [];
    state.added.concat(state.stable).forEach(function (h) {
      if (h === 'transparent' || orig.indexOf(h) > -1 || nearStd(h) || extra.indexOf(h) > -1) return;
      extra.push(h);
    });
    function inImage(hex) {
      var c = hexToRgb(hex);
      return state.palette.some(function (p) { return p.hex === hex || rgbDist2(p.rgb, c) < 12 * 12; });
    }
    grid.innerHTML = '';
    var t = document.createElement('button');
    t.type = 'button'; t.className = 'rcl__pc rcl__pc--none'; t.dataset.target = 'transparent';
    t.innerHTML = '<span class="rcl__isck rcl__tsq"></span><span>Transparent</span>';
    t.addEventListener('click', function () { pick('transparent'); });
    grid.appendChild(t);
    function cell(hex, title, isOrig) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'rcl__pc'; b.style.background = hex; b.title = title; b.dataset.target = hex;
      if (inImage(hex)) b.innerHTML = '<span class="rcl__dot" aria-hidden="true"></span>';
      b.addEventListener('click', function () { pick(hex); });
      grid.appendChild(b);
    }
    orig.forEach(function (h) { var n = nearStd(h); cell(h, n ? n[1] : h, true); });
    STD_COLORS.forEach(function (c) { if (!covered[c[0]]) cell(c[0], c[1], false); });
    extra.forEach(function (h) { cell(h, h + ' \u00b7 your color', false); });
    var cw = document.createElement('label');
    cw.className = 'rcl__pc rcl__pc--custom'; cw.title = 'Custom color';
    cw.innerHTML = '<input type="color" aria-label="Custom color">';
    cw.querySelector('input').addEventListener('change', function (e) {
      state.customHex = e.target.value.toUpperCase();
      pick(state.customHex);
    });
    grid.appendChild(cw);
  }

  function pick(target) {
    var s = state.sel;
    if (!s) return;
    if (target !== 'transparent' && state.added.indexOf(target) < 0) state.added.push(target);
    if (!s.op) {
      if (target === 'transparent' && s.transparent) return; /* already see-through */
      s.op = s.scope === 'all'
        ? { type: 'shape', scope: 'all', color: s.color, tol: s.tol, target: target }
        : { type: 'shape', scope: 'part', fx: s.fx, fy: s.fy, tol: s.tol, target: target, seedT: !!s.transparent, via: s.via };
      state.shapes.push(s.op);
    } else {
      s.op.target = target; /* Transparent on a just-filled empty spot reverts the fill */
    }
    pushHist('sel:' + s.id);
    renderPreview(); syncControls(); updateApply();
  }

  /* ── history: one entry per selection session (another color on the same selection replaces it) ── */
  function snapshot() { return JSON.stringify({ r: state.replacements, s: state.shapes }); }
  function pushHist(session) {
    var snap = snapshot();
    if (state.hist[state.histIdx] === snap) return;
    if (session && session === state.histSession && state.histIdx === state.hist.length - 1 && state.histIdx > 0) {
      state.hist[state.histIdx] = snap;
    } else {
      state.hist = state.hist.slice(0, state.histIdx + 1);
      state.hist.push(snap);
      if (state.hist.length > 50) state.hist.shift();
      state.histIdx = state.hist.length - 1;
    }
    state.histSession = session || null;
    syncHistButtons();
  }
  function stepHist(dir) {
    var next = state.histIdx + dir;
    if (next < 0 || next >= state.hist.length) return;
    state.histIdx = next;
    var snap = JSON.parse(state.hist[next]);
    state.replacements = snap.r || {};
    state.shapes = snap.s || [];
    clearSelection();
    syncHistButtons(); renderPreview(); syncControls(); updateApply();
  }
  function syncHistButtons() {
    state.ov.querySelector('.rcl__undo').disabled = state.histIdx <= 0;
    state.ov.querySelector('.rcl__redo').disabled = state.histIdx >= state.hist.length - 1;
  }

  function syncControls() {
    var ov = state.ov, s = state.sel;
    ov.classList.toggle('has-sel', !!s);
    ov.classList.toggle('has-pick', !!s);
    var target = s && s.op ? s.op.target : null;
    ov.querySelectorAll('.rcl__sw1').forEach(function (b) {
      b.classList.toggle('is-sel', !!(s && s.scope === 'all' && s.color === b.dataset.hex));
    });
    var showTrans = !!(s && (target === 'transparent' || (s.transparent && !target)));
    ov.querySelectorAll('.rcl__pc').forEach(function (b) {
      var t = b.dataset.target;
      if (b.classList.contains('rcl__pc--custom')) return; /* back to the rainbow: a picked color gets its own swatch */
      b.classList.toggle('is-sel', t === 'transparent' ? showTrans : !!target && t === target);
    });
    /* only when it changes: re-setting the same text still swaps the text node, and every node swap re-checks the page's
       body:has() rules (~18 ms a frame on this page, snappy tools 11-03) */
    var ph = ov.querySelector('.rcl__palhint'), pht = ov.querySelector('.rcl__right--v5') ? 'Select a replacement color, or tap Transparent to make it see-through.'   /* v5: one line every time (Owen 11-04) */
      : s && s.transparent ? 'Pick a color to fill it in.' : 'Pick a new color, or tap Transparent to make it see-through.';
    if (ph.textContent !== pht) ph.textContent = pht;
    ov.querySelector('.rcl__tolin').value = s ? s.tol : SHAPE_TOL;
    syncHint();
  }

  function replacementList() {
    return Object.keys(state.replacements).map(function (hex) {
      var r = state.replacements[hex];
      return { targetColor: hex, mode: r.mode, newColor: r.newColor, flatFill: r.flatFill !== false, boundaryBias: r.boundaryBias || 0 };
    }).concat(state.shapes.map(function (s) { return JSON.parse(JSON.stringify(s)); }));
  }

  var previewTimer = null;
  function schedulePreview() {
    clearTimeout(previewTimer);
    previewTimer = setTimeout(renderPreview, 60);
  }
  /* light = a Tolerance drag frame (By Size v5, snappy tools 11-03): the edits BEFORE the one being adjusted don't change
     while it drags, so they replay once and every frame starts from that picture (same steps, same order = the same
     pixels), and the Step 1 swatches wait for the release (the Tolerance 'change' handler). Everything else renders in full, as before. */
  function renderPreview(light) {
    if (!state || !state.workingBase) return;
    var b = state.workingBase, copy, masks0 = null, from = 0;
    var list = replacementList();
    var legacy = colorOnly(list), shapes = shapeOnly(list);
    var k = light && state.sel && state.sel.op ? state.shapes.indexOf(state.sel.op) : -1;
    var pkey = k > 0 ? JSON.stringify([legacy, shapes.slice(0, k)]) : null, pc = state.prefix;
    if (pkey && pc && pc.key === pkey && pc.base === b) {
      copy = new ImageData(new Uint8ClampedArray(pc.data), b.width, b.height); masks0 = pc.masks; from = k;
    } else {
      copy = new ImageData(new Uint8ClampedArray(b.data), b.width, b.height);
      if (legacy.length && !state.legacyPalette) state.legacyPalette = extractSwatches(b).map(function (s) { return s.hex; });
      if (legacy.length) applyColorReplacements(copy.data, legacy, state.legacyPalette);
      if (pkey) {
        masks0 = applyShapeOps(copy.data, b.width, b.height, shapes.slice(0, k)); from = k;
        state.prefix = { key: pkey, base: b, data: new Uint8ClampedArray(copy.data), masks: masks0 };
      }
    }
    state.opMasks = applyShapeOps(copy.data, b.width, b.height, shapes.slice(from), masks0);
    state.current = copy;
    if (!state.comparing) state.previewCanvas.getContext('2d').putImageData(copy, 0, 0);
    if (!light) { state.palette = paletteOf(visibleOf(copy), state.added); buildSwatchRow(); }
    syncControls();
    drawOverlay();
  }
  /* adapter.viewOnly (By Size v5, Owen 11-02 "chips only from visible"): the swatches come from the region shown (the
     crop), never from art outside it. Without viewOnly: the whole picture, as today. */
  function visibleOf(img) {
    var v = state && state.viewOnly && state.view;
    if (!v || !(v.w > 0 && v.h > 0)) return img;
    var x0 = Math.max(0, Math.floor(v.x * img.width)), y0 = Math.max(0, Math.floor(v.y * img.height));
    var w = Math.max(1, Math.min(img.width - x0, Math.round(v.w * img.width))), h = Math.max(1, Math.min(img.height - y0, Math.round(v.h * img.height)));
    var out = new ImageData(w, h), src = img.data, dst = out.data;
    for (var y = 0; y < h; y++) dst.set(src.subarray(((y0 + y) * img.width + x0) * 4, ((y0 + y) * img.width + x0 + w) * 4), y * w * 4);
    return out;
  }
  function updateApply() {
    state.ov.querySelector('.rcl__apply').disabled = !replacementList().length;
    /* v5 tool bar (Owen 11-04): Reset shows only when it would do something — swaps / picks made here, or colours
       already swapped on the design (adapter.canReset). Same button, same handler. */
    var rs = state.ov.querySelector('.ntbar .rcl__reset');
    if (rs) rs.hidden = !(state.adapter.canReset() || replacementList().length || (state.shapes || []).length);   /* not state.added: colours offered in the row, not an edit (Undo keeps them) */
  }

  function setBusy(msg) {
    state.busyEl.style.display = 'flex';
    state.busyEl.querySelector('.rcl__spin').style.display = '';
    state.busyEl.querySelector('span:last-child').textContent = msg;
  }

  function applyAll() {
    var repl = replacementList();
    if (!repl.length) return;
    var btn = state.ov.querySelector('.rcl__apply');
    btn.disabled = true;
    setBusy('Applying colors…');
    var adapter = state.adapter, baseSrc = state.baseSrc, palette = (state.legacyPalette || []).slice();
    var pixelSrc = state.resolvedSrc || baseSrc;
    processAndHost(pixelSrc, repl, palette).then(function (hostedUrl) {
      setBusy(adapter.busyApplyMsg || 'Saving…');
      return adapter.apply(hostedUrl, repl, baseSrc, palette);
    }).then(function () {
      closeDialog();
      toast('Colors updated.');
    }).catch(function (err) {
      console.error('[studio-recolor] apply failed', err);
      if (state) {
        state.busyEl.style.display = 'none';
        btn.disabled = false;
      }
      toast('Sorry — recoloring failed. Please try again.');
    });
  }

  function resetImage() {
    var adapter = state.adapter;
    if (!adapter.canReset()) {
      state.replacements = {};
      state.shapes = []; state.added = []; state.stable = [];
      clearSelection();
      pushHist();
      renderPreview(); syncControls(); updateApply();
      return;
    }
    setBusy('Restoring original…');
    Promise.resolve(adapter.reset()).then(function () {
      closeDialog();
      toast('Original colors restored.');
    }).catch(function (err) {
      console.error('[studio-recolor] reset failed', err);
      if (state) state.busyEl.style.display = 'none';
      toast('Sorry — could not restore the original.');
    });
  }

  function closeDialog() {
    stopAnts();
    if (state && state.onResize) window.removeEventListener('resize', state.onResize);
    if (state && state.ov) state.ov.remove();
    state = null;
  }

  function toast(msg) {
    var t = document.createElement('div');
    t.textContent = msg;
    t.style.cssText = 'position:fixed;left:50%;bottom:32px;transform:translateX(-50%);background:#0f172a;color:#fff;font:600 14px/1.4 "DM Sans",system-ui,sans-serif;padding:12px 20px;border-radius:10px;box-shadow:0 6px 24px rgba(15,23,42,.35);z-index:100001;max-width:min(480px,90vw);text-align:center;';
    document.body.appendChild(t);
    setTimeout(function () { t.remove(); }, 4000);
  }

  /* ───────────────────── fabric (Design Studio) adapter ───────────────────── */

  function fabricAdapter(fImg) {
    return {
      baseSrc: fImg._preRecolorSrc || (fImg.getSrc ? fImg.getSrc() : fImg._originalUrl),
      seedReplacements: fImg._recolorReplacements || [],
      canReset: function () { return !!fImg._preRecolorSrc; },
      apply: function (hostedUrl, repl, baseSrc) {
        return new Promise(function (resolve) {
          fImg.setSrc(hostedUrl, function () {
            if (!fImg._preRecolorSrc) fImg._preRecolorSrc = baseSrc;
            fImg._recolorReplacements = repl;
            fImg._originalUrl = hostedUrl;
            var canvas = window.fabricCanvas;
            fImg.setCoords(); canvas && canvas.requestRenderAll();
            try { if (typeof window.updateLayers === 'function') window.updateLayers(); } catch (_) {}
            try { if (typeof window.saveState === 'function') window.saveState(); } catch (_) {}
            resolve();
          }, { crossOrigin: 'anonymous' });
        });
      },
      reset: function () {
        return new Promise(function (resolve) {
          var orig = fImg._preRecolorSrc;
          fImg.setSrc(orig, function () {
            fImg._preRecolorSrc = null;
            fImg._recolorReplacements = null;
            fImg._originalUrl = orig;
            var canvas = window.fabricCanvas;
            fImg.setCoords(); canvas && canvas.requestRenderAll();
            try { if (typeof window.saveState === 'function') window.saveState(); } catch (_) {}
            resolve();
          }, { crossOrigin: 'anonymous' });
        });
      }
    };
  }

  /* studio toolbar wiring — CAPTURE phase: the pill toolbar stops click
     propagation for its own popover management. */
  if (!IN_WORKER) document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('.tft-recolor');
    if (!btn) return;
    var canvas = window.fabricCanvas;
    var obj = canvas && canvas.getActiveObject && canvas.getActiveObject();
    if (!obj || obj.type !== 'image') return; /* images only — never text */
    openRecolor(fabricAdapter(obj));
  }, true);

  /* ───────────────────── by-size PDP adapter + injection ─────────────────────
     Mirrors the Edit-with-AI apply path in assets/bySize_popular.js
     (#use_ai_image handler): processFileOrUrl -> singleFileManageByURL for
     single-upload, or updateFileDetailsFunction + toggle re-trigger for a
     multi-upload block. Those helpers plus `uploadType` are top-level
     declarations in bySize_popular.js — reachable as globals here. */

  function pdpHelpersReady() {
    try {
      return typeof processFileOrUrl === 'function' &&
             typeof getFileDetailsFunction === 'function' &&
             typeof singleFileManageByURL === 'function';
    } catch (_) { return false; }
  }

  /* ── bg-removal candidate adoption (Justin 7-14) ─────────────────────────
     Options 2/3 of the Remove-BG picker swap the preview hero to a DIFFERENT
     hosted candidate FILE (bg-removal-multi raster results) — they are not
     imgix params on the fd file, so params-grafting alone can't carry them
     into the Recolor / Image Effects dialogs (the modals showed the design
     WITH its background back). When the hero's PATH differs from the
     un-edited fd base, the candidate IS the design the customer sees: adopt
     it — minus display sizing — as the dialog's working base so the palette,
     every filter tile and Apply all run on the transparent version. Callers
     must skip this when opening on stored pre-edit bookkeeping (the hero
     shows the already-edited file then). imgix-served heroes only —
     candidates always are; placeholders/blob:/data: URLs never match. */
  function pdpHeroSrc(blockNo) {
    try {
      var $ = window.jQuery;
      var isMulti = !!(typeof uploadType !== 'undefined' && uploadType === 'multi' && blockNo);
      if (isMulti) return $('multi-upload uploaded-files-block[block-no="' + blockNo + '"] preview-box .fileupload_hero').attr('src') || '';
      return $('upload-controls preview-box .fileupload_hero').attr('src') || $('#fileupload_hero').attr('src') || '';
    } catch (_) { return ''; }
  }
  function adoptHeroBase(current, blockNo) {
    var hs = String(pdpHeroSrc(blockNo) || '');
    var hp = hs.split('?')[0];
    if (!/\.imgix\.net\//.test(hp) || !current || hp === String(current).split('?')[0]) return current;
    var out = hp;
    var q = hs.indexOf('?') > -1 ? hs.slice(hs.indexOf('?') + 1) : '';
    q.split('&').filter(Boolean).forEach(function (kv) {
      if (!/^(w|h|dpr|fit|max-w|max-h)$/.test(kv.split('=')[0])) out += (out.indexOf('?') > -1 ? '&' : '?') + kv;
    });
    return out;
  }

  function pdpAdapter(blockNo) {
    var fd = null;
    try { fd = getFileDetailsFunction(blockNo || 1) || null; } catch (_) {}
    var isMulti = false;
    try { isMulti = !!(typeof uploadType !== 'undefined' && uploadType === 'multi' && blockNo); } catch (_) {}
    var current = '';
    if (fd) current = fd.imgIxFile || fd.originalFile || '';
    if (!current && !isMulti) {
      try { if (typeof getCurrentPreviewImageUrl === 'function') current = getCurrentPreviewImageUrl() || ''; } catch (_) {}
    }
    var pre = (fd && fd.ntPreRecolorSrc) || '';
    if (!pre) current = adoptHeroBase(current, blockNo);

    /* The dialog must open showing exactly what the PREVIEW shows (Justin
       7-9): the preview hero bakes its imgix processing into the URL query
       (bg-remove=true, upscale=true, trim=colorUnlessAlpha, fm=png, ...).
       Graft the hero's own params — minus display sizing (w/h/dpr/fit),
       which would downres the recolor source — onto the modal's base.
       Fallback when no hero is mounted: the theme's getToggleOptionParams
       (live checkbox state, single/multi aware). Bookkeeping
       (ntPreRecolorSrc / reset) stays on the CLEAN base so reset restores
       the un-processed original and the toggles keep working on it. */
    var SIZE_PARAMS = /^(w|h|dpr|fit|max-w|max-h)$/;
    function heroSrc() { return pdpHeroSrc(blockNo); }
    function graft(url, query) {
      var out = url;
      query.split('&').filter(Boolean).forEach(function (kv) {
        var key = kv.split('=')[0];
        if (!SIZE_PARAMS.test(key) && out.indexOf(key + '=') === -1) {
          out += (out.indexOf('?') > -1 ? '&' : '?') + kv;
        }
      });
      return out;
    }
    function withPreviewParams(url) {
      if (!url || url.indexOf('http') !== 0) return url;
      var hs = heroSrc();
      var q = hs.indexOf('?') > -1 ? hs.slice(hs.indexOf('?') + 1) : '';
      if (q) return graft(url, q);
      var p = '';
      try {
        if (typeof getToggleOptionParams === 'function') p = getToggleOptionParams(blockNo) || '';
      } catch (_) {}
      return p ? graft(url, p) : url;
    }

    function pushThrough(url, repl, preSrc) {
      /* repl === null -> reset (clear the recolor bookkeeping) */
      return processFileOrUrl(url, blockNo || 1).then(function (uploaded) {
        var prev = null;
        try { prev = getFileDetailsFunction(blockNo || 1) || {}; } catch (_) { prev = {}; }
        var fileDetail = {
          originalFile: uploaded.originalFile || url,
          imgIxFile: uploaded.imgIxFile || uploaded.originalFile || url,
          file_crc: uploaded.file_crc,
          fileName: uploaded.fileName || 'recolor.png',
          fileURL: uploaded.originalFile || url,
          ntPreRecolorSrc: repl ? (prev.ntPreRecolorSrc || preSrc) : '',
          ntRecolorReplacements: repl || '',
          /* the customer's untouched upload → _Original Image (never the recolored
             file; Justin 2026-09-28). First recolor captures it; later recolors and
             the reset keep the one already captured. */
          originalUploadReference: prev.originalUploadReference || (repl ? (prev.originalFile || '') : '')
        };
        if (isMulti) {
          var merged = Object.assign({}, prev, fileDetail);
          var ok = false;
          ok = updateFileDetailsFunction(merged, blockNo);
          if (!ok) throw new Error('updateFileDetails failed');
          try {
            var $ = window.jQuery;
            $('multi-upload uploaded-files-block[block-no="' + blockNo + '"] toggle-options .toggleOption').trigger('change');
          } catch (_) {}
          return;
        }
        return Promise.resolve(singleFileManageByURL(fileDetail)).then(function () {
          persistSingleNt({
            ntPreRecolorSrc: fileDetail.ntPreRecolorSrc,
            ntRecolorReplacements: fileDetail.ntRecolorReplacements
          });
        });
      });
    }

    var cleanBase = pre || current;
    return {
      baseSrc: withPreviewParams(cleanBase),
      /* graft the LIVE toggle state (bg-remove / upscale / trim…) onto any URL —
         Image Effects runs its own base through this so every filter tile shows
         the design as the customer currently sees it (Justin 7-10) */
      graftPreview: withPreviewParams,
      seedReplacements: (fd && fd.ntRecolorReplacements) || [],
      busyApplyMsg: 'Applying to your design…',
      canReset: function () { return !!pre; },
      /* preSrc bookkeeping uses cleanBase, NOT the params-grafted baseSrc
         the dialog passes back — reset must restore the raw original */
      apply: function (hostedUrl, repl) { return pushThrough(hostedUrl, repl, cleanBase); },
      reset: function () { return pushThrough(pre, null, ''); }
    };
  }

  /* ───────────────────────── Image Effects (Justin 7-10) ─────────────────────
     Third AI Edit Tools row. Filters are pure imgix stylize params
     (docs.imgix.com rendering/stylize): duotone / blur / monochrome / px /
     sepia. Tap-to-preview grid (Original + 5), Apply feeds the param'd imgix
     URL through the SAME pushThrough pipeline Recolor uses, so preview,
     sizing, pricing and cart data update identically. Non-destructive:
     ntPreEffectSrc/ntEffectKey bookkeeping mirrors Recolor's — re-editing
     never stacks effects, and Original restores the true base. blur/px are
     OUTPUT-resolution dependent, so apply-time values scale from the source's
     real PixelWidth (imgix fm=json) to match what the 320px preview showed. */
  /* Single-upload file-details is REBUILT from a key allowlist inside the
     engine's singleFileManageByURL — our nt* bookkeeping isn't on that list,
     so on single upload a re-open lost the pre-effect/pre-recolor base and
     the applied key (Justin 7-12: "go back to image effect should still show
     all options including original with the most recent effect selected";
     multi survives because updateFileDetailsFunction MERGES). Merge the keys
     back into the DOM json right after the engine finishes, and once more
     after its async toggle churn settles. */
  function persistSingleNt(keys) {
    function once() {
      try {
        var $ = window.jQuery;
        var el = $('upload-controls preview-box file-details');
        var txt = el.text();
        if (!txt) return;
        var fd = JSON.parse(txt);
        Object.keys(keys).forEach(function (k) { fd[k] = keys[k]; });
        el.text(JSON.stringify(fd));
      } catch (_) {}
    }
    once();
    setTimeout(once, 1200);
  }

  var FX_LIST = [
    { key: 'original',   label: 'Original',      params: null },
    { key: 'bw',         label: 'Black & White', params: function () { return 'sat=-100'; } },
    { key: 'sepia',      label: 'Sepia',         params: function () { return 'sepia=80'; } },
    { key: 'fade',       label: 'Fade',          params: function () { return 'sat=-35&con=-15&bri=8'; } },
    { key: 'vivid',      label: 'Vivid',         params: function () { return 'vib=60&con=12&sharp=10'; } },
    { key: 'fuchsia',    label: 'Fuchsia',       params: function () { return 'monochrome=C724B1'; } },
    { key: 'cool',       label: 'Cool',          params: function () { return 'hue=15&sat=10'; } },
    { key: 'warm',       label: 'Warm',          params: function () { return 'hue=-10&sat=15&bri=5'; } },
    { key: 'duotone',    label: 'Duotone',       params: function () { return 'duotone=003087,00C5FF&duotone-alpha=100'; } },
    /* Instagram-style composites (imgix adjustment-param recipes — Nashville +
       Valencia are imgix's own published emulations, Justin 7-10) */
    { key: 'nashville',  label: 'Nashville',     params: function () { return 'sepia=20&con=15&bri=8&sat=15&duotone=F7B099,2D5C99&duotone-alpha=20'; } },
    { key: 'valencia',   label: 'Valencia',      params: function () { return 'sepia=15&con=-5&exp=8&high=-15&shad=15&sat=-10&vib=10'; } },
    { key: 'clarendon',  label: 'Clarendon',     params: function () { return 'con=20&sat=35&bri=5'; } },
    { key: 'reyes',      label: 'Reyes',         params: function () { return 'sepia=22&con=-15&bri=12&sat=-25'; } },
    { key: 'lark',       label: 'Lark',          params: function () { return 'exp=8&sat=20&con=-5&bri=4'; } },
    { key: 'juno',       label: 'Juno',          params: function () { return 'con=12&sat=25&hue=-5&high=8'; } }
  ];
  function fxJoin(url, q) { return q ? url + (url.indexOf('?') > -1 ? '&' : '?') + q : url; }

  /* ── OUR filter engine (Owen 9-11: "how do we get this to our PDP image
     filters?") ─────────────────────────────────────────────────────────────
     assets/nt-filters.js = the Design Studio's src/filters.js built standalone
     (window.NTFilters = { FILTERS, applyPresetToCanvas, fromImgixParams }):
     color-matrix math + blend overlays, the Instagram-style recipe set, alpha
     re-masked for print, Safari-safe. Loaded on demand the first time the
     Image Filters modal opens. With it the modal renders every tile LOCALLY
     from ONE base load and applies at print res on canvas — ~17 imgix renders
     per open (16 param tiles + fm=json) become 1–2 plain loads. FX_LIST below
     stays as the FALLBACK when the engine or the CORS base load fails
     (identical to the pre-9-11 imgix-param behaviour).
     The engine URL = our own script src with the filename swapped, SAME ?v=
     — build.sh emits both from one tree; always deploy them together. A host
     may pin window.__ntFiltersUrl instead. */
  var ntFiltersP = null;
  function ntFiltersUrl() {
    if (window.__ntFiltersUrl) return window.__ntFiltersUrl;
    return RCL_SRC ? RCL_SRC.replace(/studio-recolor(\.min)?\.js/, 'nt-filters.js') : '';
  }
  function loadNTFilters() {
    if (window.NTFilters && typeof window.NTFilters.applyPresetToCanvas === 'function') return Promise.resolve(window.NTFilters);
    if (ntFiltersP) return ntFiltersP;
    ntFiltersP = new Promise(function (resolve, reject) {
      var url = ntFiltersUrl();
      if (!url) { reject(new Error('nt-filters url unknown')); return; }
      var sc = document.createElement('script');
      sc.src = url;
      sc.async = true;
      sc.onload = function () {
        if (window.NTFilters && typeof window.NTFilters.applyPresetToCanvas === 'function') resolve(window.NTFilters);
        else reject(new Error('NTFilters missing after load'));
      };
      sc.onerror = function () { reject(new Error('nt-filters load failed')); };
      document.head.appendChild(sc);
    });
    ntFiltersP.catch(function () { ntFiltersP = null; }); /* a later open retries */
    return ntFiltersP;
  }
  /* find a recipe by key — the engine's list first (when loaded), then the
     legacy FX_LIST; `list` pins the search to one list */
  function fxFind(key, list) {
    var hit = null;
    var lists = list ? [list] : [(window.NTFilters && window.NTFilters.FILTERS) || [], FX_LIST];
    lists.forEach(function (l) { if (!hit) l.forEach(function (x) { if (x.key === key) hit = x; }); });
    return hit;
  }

  function fxPush(url, effectKey, preSrc, blockNo) {
    var isMulti = false;
    try { isMulti = !!(typeof uploadType !== 'undefined' && uploadType === 'multi' && blockNo); } catch (_) {}
    var fxLabel = '';
    if (effectKey) { var fxHit = fxFind(effectKey); if (fxHit) fxLabel = fxHit.label; }
    return processFileOrUrl(url, blockNo || 1).then(function (uploaded) {
      var prev = null;
      try { prev = getFileDetailsFunction(blockNo || 1) || {}; } catch (_) { prev = {}; }
      var fileDetail = {
        originalFile: uploaded.originalFile || url,
        imgIxFile: uploaded.imgIxFile || uploaded.originalFile || url,
        file_crc: uploaded.file_crc,
        fileName: uploaded.fileName || 'effect.png',
        fileURL: uploaded.originalFile || url,
        ntPreEffectSrc: effectKey ? (prev.ntPreEffectSrc || preSrc) : '',
        ntEffectKey: effectKey || '',
        /* display label rides along so the cart-add builder can stamp the
           _image effect order property without duplicating FX_LIST */
        ntEffectLabel: effectKey ? (fxLabel || effectKey) : ''
      };
      if (isMulti) {
        var merged = Object.assign({}, prev, fileDetail);
        if (!updateFileDetailsFunction(merged, blockNo)) throw new Error('updateFileDetails failed');
        try {
          var $ = window.jQuery;
          $('multi-upload uploaded-files-block[block-no="' + blockNo + '"] toggle-options .toggleOption').trigger('change');
        } catch (_) {}
        return;
      }
      return Promise.resolve(singleFileManageByURL(fileDetail)).then(function () {
        persistSingleNt({
          ntPreEffectSrc: fileDetail.ntPreEffectSrc,
          ntEffectKey: fileDetail.ntEffectKey,
          ntEffectLabel: fileDetail.ntEffectLabel
        });
      });
    });
  }

  function openEffects(blockNo, adapter) {
    /* ⚠ BSV4 BRANCH — DO NOT DROP (see initPdp): on the by-size-v4 PDP the
       bundle owns file state and hands us an adapter
       { baseSrc, previewBase, appliedKey, push(url, key, preSrc) } via
       window.__bsv4OpenEffects; absent = the legacy fileDetails path below,
       unchanged. */
    var fd = null, current = '', pre = '', appliedKey = 'original', previewBase = '';
    if (adapter) {
      /* localBase (bsv4, Owen 2026-09-14): an already-rendered bg-removed URL
         (persistent w=2000, in browser cache). Preview + apply on it WITHOUT
         grafting sizing params, so no imgix bg-remove render fires during the
         popup and the filtered output keeps the background removed. */
      current = adapter.localBase || adapter.baseSrc || '';
    } else {
      try { fd = getFileDetailsFunction(blockNo || 1) || null; } catch (_) {}
      current = fd ? (fd.imgIxFile || fd.originalFile || '') : '';
      if (!current && !blockNo) {
        try { if (typeof getCurrentPreviewImageUrl === 'function') current = getCurrentPreviewImageUrl() || ''; } catch (_) {}
      }
      pre = (fd && fd.ntPreEffectSrc) || '';
      if (!pre) current = adoptHeroBase(current, blockNo);
    }
    var cleanBase = pre || current;
    if (!cleanBase) { toast('Upload an image first, then add an effect.'); return; }
    appliedKey = adapter ? (adapter.appliedKey || 'original') : ((fd && fd.ntEffectKey) || 'original');

    /* Image Effects are PURE imgix params — a raw-S3 base (Design Library /
       ready-to-press loads live on s3.amazonaws.com/ninja-imgix, which
       ignores query params) rendered 15 identical tiles and Apply was a
       no-op (Justin 7-13). Resolve such bases to an imgix-served twin (the
       rtp-mirror mapping Recolor's corsCandidates already uses) before
       building the grid; when no imgix-capable twin loads (e.g. a design
       newer than the mirror batch), say so instead of showing a dead grid. */
    function fxIsImgix(u) { return /\.imgix\.net\//.test(String(u || '')); }
    if (fxIsImgix(cleanBase)) { buildEffects(cleanBase); return; }
    var fxCands = corsCandidates(cleanBase).filter(fxIsImgix);
    if (!fxCands.length) { toast("Image Filters aren't available for this design."); return; }
    (function tryCand(i) {
      if (i >= fxCands.length) { toast("Image Filters aren't available for this design."); return; }
      var probe = new Image();
      probe.onload = function () { buildEffects(fxCands[i]); };
      probe.onerror = function () { tryCand(i + 1); };
      probe.src = fxJoin(fxCands[i], 'w=64');
    })(0);
    return;

    function buildEffects(cleanBase) {
    /* preview tiles show the params-grafted view — Remove BG / Upscale state
       carries into EVERY filter tile, including re-opens after a previous
       effect (Justin 7-10). When the base was twin-resolved above, carry the
       adapter previewBase's processing params onto the twin. */
    /* fxLocal: the base is already the final bg-removed render — tiles load it
       AS-IS (cache hit, no bg-remove render) and Apply reuses those pixels. */
    var fxLocal = !!(adapter && adapter.localBase);
    previewBase = cleanBase;
    if (fxLocal) {
      previewBase = cleanBase;
    } else if (adapter) {
      var apb = adapter.previewBase || '';
      if (apb && apb.split('?')[0] === (adapter.baseSrc || '').split('?')[0]) {
        var q = apb.indexOf('?') > -1 ? apb.slice(apb.indexOf('?') + 1) : '';
        previewBase = q ? fxJoin(cleanBase, q) : cleanBase;
      } else if (apb && fxIsImgix(apb)) {
        previewBase = apb;
      }
    } else {
      try { previewBase = pdpAdapter(blockNo).graftPreview(cleanBase) || cleanBase; } catch (_) {}
    }

    injectCss();
    var ov = document.createElement('div');
    ov.className = 'rcl-ov fx-ov';
    ov.innerHTML =
      '<div class="rcl fx">' +
        '<div class="rcl__head"><h3 class="rcl__title">Image Filters</h3>' +
        '<button type="button" class="rcl__close" aria-label="Close"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>' +
        '<p class="fx__hint">Tap a filter to preview it on your design.</p>' +
        '<div class="fx__grid"></div>' +
        '<div class="rcl__foot"><span></span><div style="display:flex;gap:10px">' +
          '<button type="button" class="rcl__btn rcl__btn--ghost fx__cancel">Cancel</button>' +
          '<button type="button" class="rcl__btn rcl__btn--primary fx__apply" disabled>Apply</button>' +
        '</div></div>' +
      '</div>';
    document.body.appendChild(ov);

    var sel = appliedKey;
    var applyBtn = ov.querySelector('.fx__apply');
    var grid = ov.querySelector('.fx__grid');
    var sheet = ov.querySelector('.rcl');
    var view = adapter && adapter.viewRect, viewOn = !!view, fxDims = null;
    function fxView(iw, ih) {
      if (!view || fxDims || !(iw > 0 && ih > 0)) return;
      var R = iw * view.w / (ih * view.h), rw = R >= 1 ? 1 : R, rh = R >= 1 ? 1 / R : 1;
      fxDims = true;
      grid.style.setProperty('--fxw', rw / view.w * 100 + '%');
      grid.style.setProperty('--fxh', rh / view.h * 100 + '%');
      grid.style.setProperty('--fxl', ((1 - rw) / 2 - view.x * rw / view.w) * 100 + '%');
      grid.style.setProperty('--fxt', ((1 - rh) / 2 - view.y * rh / view.h) * 100 + '%');
      grid.classList.toggle('fx--view', viewOn);
    }
    if (view) grid.insertAdjacentElement('beforebegin', viewToggle(function (on) { viewOn = on; grid.classList.toggle('fx--view', on && !!fxDims); }));
    var list = FX_LIST;   /* the recipe list the grid shows: engine FILTERS or legacy FX_LIST */
    var local = false;    /* true = OUR engine renders tiles + Apply (no imgix params) */
    var baseImg = null;   /* the one 640px source load (local path) */
    var base320 = null;   /* its 320px working copy every tile renders from */
    var tileImg = null;   /* what the tiles show: baseImg, or adapter.thumbSrc (v5: the design as on screen) */
    /* Intensity (Owen 9-11 "just the simple path on pdp"): 0–100 strength of
       the picked filter, the studio's Filters-pane slider on the PDP. Lives
       directly under the picked tile's ROW (studio rule: option controls
       below their row), scrubs that ONE tile live (320px = instant), resets
       to 100 on a new pick (Canva), hidden on Original and on the imgix
       fallback (param recipes have no strength). Baked in at Apply; the label
       carries it ("Clarendon 70%"). Re-opening shows 100 — the record keeps
       key + label only, and Apply always re-renders from the clean base. */
    /* seed from the applied intensity so re-opening restores the slider (Owen
       2026-09-14) — the adapter carries appliedStrength; placeCtrls writes it to
       the range/label when the applied filter is the selected one. A fresh pick
       still resets to 100 (Canva) below. */
    var strength = (adapter && typeof adapter.appliedStrength === 'number') ? adapter.appliedStrength : 100, strengthTouched = false;
    var ctrls = document.createElement('div');
    ctrls.className = 'rcl__tol fx__ctrls';
    ctrls.innerHTML = '<span class="fx__ctrls-lab">Intensity</span>' +
      '<input type="range" min="0" max="100" value="100" step="1" aria-label="Filter intensity">' +
      '<span class="val">100%</span>';
    var strengthRange = ctrls.querySelector('input');
    var strengthVal = ctrls.querySelector('.val');
    function placeCtrls() {
      if (!local || sel === 'original' || !base320) { if (ctrls.parentNode) ctrls.remove(); return; }
      var cards = [].slice.call(grid.querySelectorAll('.fx__card'));
      var idx = -1;
      cards.forEach(function (c, i) { if (c.getAttribute('data-fx') === sel) idx = i; });
      if (idx < 0) { if (ctrls.parentNode) ctrls.remove(); return; }
      var cols = 1;
      try { cols = (getComputedStyle(grid).gridTemplateColumns || '').split(' ').filter(Boolean).length || 1; } catch (_) {}
      var rowEnd = cards[Math.min(cards.length - 1, Math.floor(idx / cols) * cols + (cols - 1))];
      if (ctrls.previousElementSibling !== rowEnd) rowEnd.insertAdjacentElement('afterend', ctrls);
      strengthRange.value = String(strength);
      strengthVal.textContent = strength + '%';
    }
    function renderTile(key, v) {
      if (!local || !base320 || !window.NTFilters) return;
      var f = fxFind(key, list), t = thumbOf(key);
      if (!f || !f.params || !t) return;
      var cv = window.NTFilters.applyPresetToCanvas(base320, f.params, v);
      if (cv === base320) cv = drawScaled(tileImg || baseImg, 320); /* strength 0 hands back the source — never mount the shared copy */
      t.innerHTML = '';
      t.appendChild(cv);
    }
    strengthRange.addEventListener('input', function () {
      strength = Math.max(0, Math.min(100, Math.round(parseFloat(strengthRange.value) || 0)));
      strengthTouched = true;
      strengthVal.textContent = strength + '%';
      renderTile(sel, strength);
      applyBtn.disabled = false;
    });
    function syncSel() {
      [].forEach.call(ov.querySelectorAll('.fx__card'), function (c) {
        c.classList.toggle('is-sel', c.getAttribute('data-fx') === sel);
      });
      applyBtn.disabled = (sel === appliedKey && !strengthTouched);
      placeCtrls();
    }
    var busyEl = null;
    function busyOn(msg) {
      busyOff();
      busyEl = document.createElement('div');
      busyEl.className = 'rcl__busy';
      busyEl.innerHTML = '<div class="rcl__spin"></div><div style="font-size:13px;color:#374151">' + msg + '</div>';
      sheet.appendChild(busyEl);
    }
    function busyOff() { if (busyEl) { busyEl.remove(); busyEl = null; } }
    function renderGrid(items) {
      grid.innerHTML = items.map(function (f) {
        return '<button type="button" class="fx__card" data-fx="' + f.key + '">' +
          '<span class="fx__thumb"></span>' +
          '<span class="fx__label">' + f.label + '</span></button>';
      }).join('');
      syncSel();
    }
    function thumbOf(key) {
      var c = grid.querySelector('.fx__card[data-fx="' + key + '"]');
      return c ? c.querySelector('.fx__thumb') : null;
    }
    /* Tiles. Local path: ONE base load (≤640px, CORS-anonymous — imgix serves
       CORS) -> a 320px working copy -> every recipe rendered on canvas.
       Fallback (engine or CORS load failed): the imgix param tiles exactly as
       before, 16 renders. */
    busyOn('Loading filters…');
    loadNTFilters()
      .then(function (NT) {
        /* fxLocal: load the persistent bg-removed render verbatim (cache hit,
           no new imgix render); else the small preview derivative as before. */
        return loadImage(fxLocal ? previewBase : fxJoin(previewBase, 'w=640&auto=format')).then(function (img) {
          /* adapter.thumbSrc (By Size v5, Owen 11-02, opt-in): the tiles show the design AS ON SCREEN (crop, edits, live
             text — the filter covers all of it); Apply still renders from the base above. A failed load keeps today's
             tiles. No thumbSrc = exactly today's behaviour. */
          var ts = adapter && adapter.thumbSrc;
          if (!ts) return { img: img, tile: img };
          return Promise.resolve(typeof ts === 'function' ? ts() : ts).then(function (u) { return u ? loadImage(u) : img; })
            .then(function (t) { return { img: img, tile: t || img, own: !!(t && t !== img) }; }, function () { return { img: img, tile: img }; });
        }).then(function (r) {
          var img = r.img;
          local = true;
          list = NT.FILTERS;
          renderGrid(list);
          baseImg = img;
          if (r.own) { view = null; viewOn = false; var vt = grid.previousElementSibling; if (vt && vt.classList && vt.classList.contains('rcl__view')) vt.remove(); }
          else fxView(img.naturalWidth, img.naturalHeight);
          tileImg = r.tile;
          base320 = drawScaled(r.tile, 320);
          list.forEach(function (f) {
            var t = thumbOf(f.key);
            if (!t) return;
            t.appendChild(f.params ? NT.applyPresetToCanvas(base320, f.params, 100) : drawScaled(tileImg, 320));
          });
          /* re-open at a saved <100 intensity: show the applied tile at that
             strength so its thumbnail matches the restored slider (Owen 9-14) */
          if (sel !== 'original' && strength !== 100) renderTile(sel, strength);
          syncSel(); /* Intensity row under the already-applied filter, if any */
        });
      })
      .catch(function (err) {
        console.warn('[image-filters] local engine unavailable — imgix tiles', err && err.message);
        local = false;
        list = FX_LIST;
        renderGrid(list);
        list.forEach(function (f) {
          var t = thumbOf(f.key);
          if (!t) return;
          var im = document.createElement('img');
          im.loading = 'lazy';
          im.alt = f.label;
          im.onload = function () { fxView(im.naturalWidth, im.naturalHeight); };
          im.src = f.params ? fxJoin(previewBase, f.params(1) + '&w=320&auto=format') : fxJoin(previewBase, 'w=320&auto=format');
          t.appendChild(im);
        });
      })
      .then(function () { if (ov.isConnected) busyOff(); });
    function close() { ov.remove(); document.removeEventListener('keydown', onKey); window.removeEventListener('resize', placeCtrls); }
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', placeCtrls);
    ov.addEventListener('click', function (e) {
      if (e.target === ov) { close(); return; }
      var card = e.target.closest && e.target.closest('.fx__card');
      if (card) {
        var k = card.getAttribute('data-fx');
        if (k !== sel) {
          if (strength !== 100) renderTile(sel, 100);
          sel = k;
          strength = 100;
          strengthTouched = false;
        }
        syncSel();
        /* a pick near the bottom of a 2-column phone grid mounts the row
           off-screen — bring it (minimally) into view */
        if (ctrls.parentNode && ctrls.scrollIntoView) { try { ctrls.scrollIntoView({ block: 'nearest', behavior: 'smooth' }); } catch (_) {} }
        return;
      }
      if (e.target.closest && (e.target.closest('.rcl__close') || e.target.closest('.fx__cancel'))) { close(); return; }
      if (e.target.closest && e.target.closest('.fx__apply') && !applyBtn.disabled) {
        busyOn('Applying to your design…');
        var job;
        var blobUrl = '';
        var push = adapter
          ? adapter.push
          : function (u, k, p2) { return fxPush(u, k, p2, blockNo); };
        if (sel === 'original') {
          job = push(pre || cleanBase, null, '');
        } else {
          var f = fxFind(sel, list);
          if (local && f && f.params) {
            /* OUR engine. Load the clean base ONCE at print res (fit=max never
               upscales; MAX_SOURCE = the bound Recolor renders at), apply the
               recipe on canvas, hand the PNG on:
               • v4 adapter -> a blob: url — edit.js fetches it and re-hosts via
                 uploadBlob (one upload, zero imgix renders);
               • legacy PDP -> hosted through the presign pipeline first (fxPush
                 -> processFileOrUrl wants an https S3/imgix url).
               4th arg = display label for the _image filters order property. */
            /* fxLocal: reuse the already-loaded bg-removed pixels — no fit=max
               imgix render. baseImg is the w=2000 persistent render; drawScaled
               never upscales, so the master matches that print resolution. */
            job = (fxLocal ? Promise.resolve(baseImg)
                           : loadImage(fxJoin(cleanBase, 'fit=max&w=' + MAX_SOURCE + '&h=' + MAX_SOURCE)))
              .then(function (img) {
                return canvasToPngBlob(window.NTFilters.applyPresetToCanvas(drawScaled(img, MAX_SOURCE), f.params, strength));
              })
              .then(function (blob) {
                if (adapter) { blobUrl = URL.createObjectURL(blob); return blobUrl; }
                return hostPngBlob(blob, 'filter');
              })
              .then(function (u) { return push(u, sel, cleanBase, f.label + (strength < 100 ? ' ' + strength + '%' : ''), strength); });
          } else if (f && typeof f.params === 'function') {
            /* legacy imgix path (engine unavailable): scale blur/px to the
               source's true resolution so the applied file matches the 320px
               preview; PixelWidth via imgix fm=json, safe fallback ~1900px */
            job = fetch(fxJoin(cleanBase, 'fm=json'), { mode: 'cors' })
              .then(function (r) { return r.json(); })
              .then(function (j) { return (j && j.PixelWidth) ? j.PixelWidth / 320 : 6; })
              .catch(function () { return 6; })
              .then(function (scale) { return push(fxJoin(cleanBase, f.params(scale)), sel, cleanBase, f.label); });
          } else {
            job = Promise.reject(new Error('unknown filter ' + sel));
          }
        }
        job.then(function () { close(); }).catch(function (err) {
          console.error('[image-effects]', err);
          busyOff();
          toast('Sorry — that effect could not be applied. Please try again.');
        }).then(function () { if (blobUrl) URL.revokeObjectURL(blobUrl); });
      }
    });
    }
  }

  function initPdp() {
    /* ⚠ BSV4 BRANCH — DO NOT DROP when rebuilding this file from a local
       copy (clobbered TWICE on 2026-07-10 — now committed to main so pulls
       carry it). The by-size-v4 PDP renders its OWN "AI Edit Tools" dropdown
       whose Recolor item wears .nt-recolor-btn — only the capture click
       listener below is needed there; the legacy button injection / CSS /
       observer stay template-gated. The v4 page supplies
       window.__bsv4RecolorAdapter. */
    var isBsv4 = !!document.querySelector('[data-bsv4-root]');
    if (!PDP_TEMPLATES.test(document.body.className) && !isBsv4) return;
    if (!isBsv4) initPdpChrome();
    else window.__bsv4OpenEffects = function (adapter) { openEffects(null, adapter); };
    /* Headless render (Owen 9-23, BG option switch under an applied filter): the
       SAME pipeline the panel's Apply runs — load the source, apply the recipe on
       canvas at print res, hand back a PNG blob. opts.asIs = load the url as given
       (the persistent w=2000 bg-removed render, already in cache) instead of a
       fit=max derivative — so a switch costs ONE upload and zero new renders. */
    /* By Size v5 (2026-10-30): the same full-res Apply pipeline, headless — re-applies a customer's color swaps to another
       Remove BG option's cut-out when they switch options (edits follow the option). Returns the hosted PNG url. */
    window.__bsv4RenderRecolor = function (src, replacements, palette) { return processAndHost(src, replacements, palette || []); };
    /* By Size v5 (11-01): the same render, NOT hosted — the Remove BG option thumbnails redrawn with the edits (display only) */
    window.__bsv4RenderRecolorBlob = function (src, replacements, palette) { return processToBlob(src, replacements, palette || []); };
    /* By Size v5 Eraser (2026-10-31): the Magic Erase selection (this dialog's v1 flood / every-matching-color tests)
       + the image + dialog-shell helpers, shared so the eraser never carries its own copies. Read-only export. */
    window.__ntRclKit = { flood: floodMask, global: globalMask, loadAny: loadImageAny, cors: corsCandidates,
      drawScaled: drawScaled, toPng: canvasToPngBlob, css: injectCss, view: viewToggle, MAX_SOURCE: MAX_SOURCE, MAX_WORKING: MAX_WORKING };
    window.__bsv4RenderEffect = function (src, key, strength, opts) {
      return loadNTFilters().then(function () {
        var f = fxFind(key);
        if (!f || !f.params || typeof f.params === 'function') throw new Error('no engine recipe for filter ' + key);
        var url = opts && opts.asIs ? src : fxJoin(src, 'fit=max&w=' + MAX_SOURCE + '&h=' + MAX_SOURCE);
        var st = typeof strength === 'number' ? strength : 100;
        return loadImage(url).then(function (img) {
          return canvasToPngBlob(window.NTFilters.applyPresetToCanvas(drawScaled(img, MAX_SOURCE), f.params, st));
        });
      });
    };
    bindPdpClick();
  }

  function initPdpChrome() {
    injectPdpCss();
    /* AI Edit Tools "Image Effects" row (nt-dtf-3col.js) opens this */
    window.ntImageEffects = {
      open: function (blockNo) {
        if (!pdpHelpersReady()) { toast('Still loading — please try again in a moment.'); return; }
        openEffects(blockNo);
      }
    };

    var RECOLOR_HTML = '' +
      '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 2.2c-4.3 0-7.8 3.2-7.8 7.3 0 4 3.5 7.3 7.8 7.3.8 0 1.4-.6 1.4-1.4 0-.4-.1-.7-.4-1-.2-.3-.4-.6-.4-1 0-.8.7-1.4 1.5-1.4h1.7c2.4 0 4.3-1.8 4.3-4.1C18 4.9 14.4 2.2 10 2.2z"/><circle cx="6.3" cy="7.4" r="1.15" fill="currentColor" stroke="none"/><circle cx="10" cy="5.6" r="1.15" fill="currentColor" stroke="none"/><circle cx="13.7" cy="7.4" r="1.15" fill="currentColor" stroke="none"/><circle cx="5.6" cy="11.4" r="1.15" fill="currentColor" stroke="none"/></svg>' +
      '<span>Swap Colors</span>';

    /* Pixel-match the Edit with AI pill (Justin 7-9 "should look and hover
       just like Edit with AI"). FIXED constants of the settled Edit-pill
       computed style (measured on dtf-transfers: 1.5px dashed #CDCED1,
       radius 12, translucent white fill, 12px/600 #5A5B60 DM Sans, 45px
       tall), applied as inline !important so no cascade or CSS-load-timing
       (media=print onload swap) can strip them — a runtime clone of the Edit
       button was tried first and captured the UNSTYLED state on slower
       loads. Hover is JS-driven (inline !important defeats stylesheet
       :hover) and copies the Edit pill's rail hover EXACTLY: bySize.css's
       bare `.edit-with-ai:hover` (the only hover rule that reaches the
       rail-adopted Edit button) = shadow deepens 0 2px 6px .25 → 0 6px
       14px .18 with a translateY(-1px) lift. The earlier white-fill guess
       was imperceptible (Justin 7-9 "still no hover"); the Ninja-blue fill
       before that was the FLAT toolbar's hover, wrong in the rail. */
    var RAIL_LOOK = {
      'border-top-width': '1.5px', 'border-right-width': '1.5px',
      'border-bottom-width': '1.5px', 'border-left-width': '1.5px',
      'border-style': 'dashed', 'border-color': '#CDCED1',
      'border-radius': '12px', 'background-color': 'rgba(255,255,255,.84)',
      'padding-top': '4px', 'padding-right': '10px',
      'padding-bottom': '4px', 'padding-left': '10px',
      'font-size': '12px', 'font-weight': '600',
      'font-family': '"DM Sans", system-ui, sans-serif',
      'color': '#5A5B60', 'line-height': '1.3', 'letter-spacing': 'normal',
      '-webkit-text-fill-color': '#5A5B60', 'min-height': '45px',
      /* the Edit pill carries a soft drop shadow at rest + transitions —
         measured 7-9 (Justin: "no drop shadow like Edit with AI") */
      'box-shadow': '0 2px 6px rgba(0,0,0,.25)', 'transform': 'none',
      'transition': 'background-color .15s, box-shadow .15s, transform .15s'
    };
    var RAIL_HOVER = {
      'box-shadow': '0 6px 14px rgba(0,0,0,.18)',
      'transform': 'translateY(-1px)'
    };
    function applyLook(btn, m) { Object.keys(m).forEach(function (k) { btn.style.setProperty(k, m[k], 'important'); }); }
    function matchEditStyle(btn) {
      if (!btn) return;
      applyLook(btn, RAIL_LOOK);
      if (!btn.__ntHoverBound) {
        btn.__ntHoverBound = true;
        btn.addEventListener('mouseenter', function () { applyLook(btn, RAIL_HOVER); });
        btn.addEventListener('mouseleave', function () { applyLook(btn, RAIL_LOOK); });
      }
      /* neutralize the card BOX around our pill (the Edit card is
         transparent; ours must be too or the pill sits in a second box) */
      var card = btn.closest('.nt-ai-card--recolor');
      if (card) {
        card.style.setProperty('background', 'transparent', 'important');
        card.style.setProperty('border', '0', 'important');
        card.style.setProperty('box-shadow', 'none', 'important');
        card.style.setProperty('padding', '0', 'important');
      }
      btn.__ntStyled = true;
    }
    function matchEditStyleSettled(btn) { matchEditStyle(btn); }
    function makeBtn(flat) {
      var rb = document.createElement('button');
      rb.type = 'button';
      /* flat toolbar: wear the edit-with-ai class so the shared toolbar CSS
         styles it EXACTLY like Edit with AI (faint dashes, hover, every
         breakpoint). No .edit-with-aiFlex inside, so the AI-modal click
         binding ($('.edit-with-ai .edit-with-aiFlex')) never fires on it.
         Rail instances stay class-plain — inline constants style them. */
      rb.className = flat ? 'nt-recolor-btn edit-with-ai' : 'nt-recolor-btn';
      rb.setAttribute('aria-label', 'Swap Colors');
      rb.innerHTML = RECOLOR_HTML;
      return rb;
    }
    function injectButtons() {
      /* Desktop: nt-dtf-3col.js rebuilds the toolbar into the "AI TOOLS" rail
         and ADOPTS button.edit-with-ai into .nt-ai-card--edit (pulling it out
         of any wrapper we made earlier). Ride along: our own card directly
         under the Edit card; clean up any stranded flat-bar leftovers. */
      var editCard = document.querySelector('.nt-ai-rail .nt-ai-card--edit');
      /* never treat our own flat button as the rail's Edit pill */
      if (editCard && editCard.querySelector('.edit-with-ai') && editCard.querySelector('.edit-with-ai').classList.contains('nt-recolor-btn')) editCard = null;
      if (editCard) {
        /* Clean up ONLY stranded flat-bar leftovers — a wrapper whose Edit
           button the rail adopted away. Healthy flat pairs (multi-upload
           tiles, builder blocks) still hold their Edit button and must
           survive: the rail stays in the DOM (hidden) in multi state, and
           a global sweep here was deleting the tiles' Recolor buttons
           while data-nt-rcl kept them from ever coming back (7-9). */
        document.querySelectorAll('.nt-ai-actions').forEach(function (w) {
          if (w.querySelector('.edit-with-ai:not(.nt-recolor-btn)')) return;
          var rb = w.querySelector('.nt-recolor-btn');
          if (rb) rb.remove();
          while (w.firstChild) w.parentNode.insertBefore(w.firstChild, w);
          w.remove();
        });
        document.querySelectorAll('.nt-recolor-btn').forEach(function (b) {
          if (!b.closest('.nt-ai-card--recolor') && !b.closest('.nt-ai-actions')) b.remove();
        });
        if (!editCard.parentNode.querySelector('.nt-ai-card--recolor')) {
          var card = document.createElement('div');
          card.className = 'nt-ai-card nt-ai-card--recolor';
          var rBtn = makeBtn(false);
          card.appendChild(rBtn);
          editCard.parentNode.insertBefore(card, editCard.nextSibling);
          matchEditStyleSettled(rBtn);
        } else {
          var existing = editCard.parentNode.querySelector('.nt-ai-card--recolor .nt-recolor-btn');
          if (existing && !existing.__ntStyled) matchEditStyleSettled(existing);
        }
      }
      /* Flat toolbar (mobile, or before the rail builds): dashed pill wrapped
         with Edit with AI. Skip buttons the rail already owns. */
      document.querySelectorAll('.multiupload-rebuild-tools-inner .edit-with-ai:not([data-nt-rcl]):not(.nt-recolor-btn)').forEach(function (editBtn) {
        if (editBtn.closest('.nt-ai-card--edit')) return; /* rail owns it */
        editBtn.setAttribute('data-nt-rcl', '1');
        var wrap = document.createElement('div');
        wrap.className = 'nt-ai-actions';
        editBtn.parentNode.insertBefore(wrap, editBtn);
        wrap.appendChild(editBtn);
        wrap.appendChild(makeBtn(true));
        /* placement belongs to the wrapper — zero the buttons' own margins
           inline (the multi-upload APP's stylesheet puts a 12px !important
           margin-top on Edit in the pb builder that no theme selector can
           out-rank; it broke the uniform 7px column rhythm — Justin 7-9) */
        [].forEach.call(wrap.children, function (btn) {
          ['margin-top', 'margin-right', 'margin-bottom', 'margin-left'].forEach(function (p) {
            btn.style.setProperty(p, '0', 'important');
          });
        });
      });
    }
    injectButtons();
    /* the toolbar mounts post-upload (and per multi-upload block) — keep
       injecting as it appears. childList-only; injection marks nodes so
       this can't loop. */
    new MutationObserver(function () { injectButtons(); }).observe(document.body, { childList: true, subtree: true });
  }

  function bindPdpClick() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.nt-recolor-btn');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      /* ⚠ BSV4 BRANCH (see initPdp): the v4 bundle owns file state and hands
         us a ready adapter (same contract as pdpAdapter). */
      if (typeof window.__bsv4RecolorAdapter === 'function') {
        var a = window.__bsv4RecolorAdapter(btn);
        if (a) { if (!a.baseSrc) { toast('Upload an image first, then recolor it.'); return; } openRecolor(a); }
        return;
      }
      if (!pdpHelpersReady()) { toast('Still loading — please try again in a moment.'); return; }
      var blockEl = btn.closest('uploaded-files-block');
      var blockNo = blockEl ? blockEl.getAttribute('block-no') : null;
      var adapter = pdpAdapter(blockNo);
      if (!adapter.baseSrc) { toast('Upload an image first, then recolor it.'); return; }
      openRecolor(adapter);
    }, true);
  }

  if (IN_WORKER) {
    R2 = self.NTRecolor2 && self.NTRecolor2.plan ? self.NTRecolor2 : null;
    self.onmessage = function (e) {
      var d = e.data || {};
      window.__bsv5Tools = !!d.v5;
      workerRender(d).then(function (blob) { self.postMessage({ id: d.id, blob: blob, kind: R2_AN && R2_AN.kind }); },
        function (err) { self.postMessage({ id: d.id, error: String(err && err.message || err) }); });
    };
    return;
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initPdp);
  else initPdp();
})();
